import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth, db } from '../firebase/config';
import { doc, getDoc, setDoc, updateDoc, onSnapshot } from 'firebase/firestore';
import { theme } from '../styles/GlobalStyles';
import ResponsivePlanner from '../components/ResponsivePlanner';

interface PlannerProps {
  profileData?: any;
}

interface TimeSlot {
  time: string;
  startTime?: string;
  endTime?: string;
  date?: string;
  activity: string;
  type: 'chore' | 'lunchbox' | 'dinner' | 'task' | 'event';
  assignedTo?: string;
  assignedColor?: string;
  isCompleted: boolean;
}

interface DayPlanner {
  date: string;
  dayOfWeek: string;
  chore: string;
  choreAssignedTo?: string | null; // Household member name
  choreColor?: string | null; // Color for the assigned member
  lunchbox: string;
  lunchboxAssignedTo?: string | null; // Member responsible for cooking
  lunchboxColor?: string | null; // Color for the assigned member
  dinner: string;
  dinnerAssignedTo?: string | null; // Member responsible for cooking
  dinnerColor?: string | null; // Color for the assigned member
  timeSlots?: TimeSlot[]; // Multiple tasks throughout the day
  isCompleted: {
    chore: boolean;
    lunchbox: boolean;
    dinner: boolean;
  };
}

interface WeeklyPlanner {
  id: string;
  userId: string;
  startDate: string;
  endDate: string;
  weekNumber: number;
  year: number;
  days: DayPlanner[];
  isGenerated: boolean;
  createdAt: string;
}

interface PlannerSettings {
  autoGenerate: boolean;
  frequency: 'weekly' | 'monthly' | 'manual';
  lastGenerated?: string;
  nextGeneration?: string;
}

// Removed ViewMode type as we only use dashboard view now

// Only keep the styled components that are actually used
const NavButton = styled.button`
  background: none;
  border: none;
  color: ${theme.colors.text};
  cursor: pointer;
  padding: ${theme.spacing.sm};
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f5f5f5;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const TodayButton = styled.button`
  background: none;
  border: 1px solid #e0e0e0;
  color: ${theme.colors.text};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border-radius: 6px;
  font-weight: 500;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f5f5f5;
    border-color: ${theme.colors.primary};
  }
`;

const CreateButton = styled.button`
  background: none;
  color: ${theme.colors.primary};
  border: none;
  width: 40px;
  height: 40px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-size: 24px;
  font-weight: 700;
  line-height: 1;
  
  &:hover {
    background: #f5f5f5;
    color: ${theme.colors.secondary};
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

// Keep EventItem as it's actually used
const EventItem = styled.div.withConfig({
  shouldForwardProp: (prop) => !['completed', 'color', 'type', 'isPast'].includes(prop),
})<{ color?: string; type?: string; completed?: boolean; isPast?: boolean }>`
  background: ${props => {
    if (props.isPast) return '#e5e7eb';
    return props.color || theme.colors.primary;
  }};
  color: ${props => props.isPast ? '#6b7280' : 'white'};
  padding: 6px 8px;
  margin-bottom: 4px;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1.2;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  opacity: ${props => props.isPast ? 0.8 : 1};
  filter: ${props => props.isPast ? 'grayscale(30%)' : 'none'};
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }
`;

const WeekDayTitle = styled.div<{ isToday?: boolean }>`
  font-size: 0.85rem;
  font-weight: 600;
  color: ${props => props.isToday ? theme.colors.primary : theme.colors.lightText};
  margin-bottom: 4px;
`;

const WeekDayNumber = styled.div<{ isToday?: boolean }>`
  font-size: 1.2rem;
  font-weight: ${props => props.isToday ? '700' : '500'};
  color: ${props => props.isToday ? theme.colors.primary : theme.colors.text};
`;

const WeekGridContainer = styled.div`
  flex: 1;
  position: relative;
  overflow-y: auto;
  overflow-x: hidden;
  
  /* Hide scrollbar for Chrome, Safari and Opera */
  &::-webkit-scrollbar {
    display: none;
  }
  
  /* Hide scrollbar for IE, Edge and Firefox */
  -ms-overflow-style: none;  /* IE and Edge */
  scrollbar-width: none;  /* Firefox */
`;

const OldWeekGrid = styled.div`
  display: grid;
  grid-template-columns: 60px repeat(7, 1fr);
  min-height: 100%;
`;

const WeekTimeSlot = styled.div`
  border-right: 1px solid #e0e0e0;
  border-bottom: 1px solid #e0e0e0;
  padding: ${theme.spacing.sm};
  font-size: 0.8rem;
  color: ${theme.colors.lightText};
  text-align: center;
  height: 90px;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 4px;
  background: #fafafa;
  position: relative;
  
  &:first-child {
    border-top: 1px solid #e0e0e0;
  }
`;

const WeekDayColumn = styled.div`
  border-right: 1px solid #e0e0e0;
  border-bottom: 1px solid #e0e0e0;
  padding: ${theme.spacing.sm};
  height: 90px;
  position: relative;
  background: white;
  
  &:first-child {
    border-top: 1px solid #e0e0e0;
  }
  
  &:last-child {
    border-right: none;
  }
`;

const CurrentTimeLine = styled.div<{ top: number; dayIndex: number }>`
  position: absolute;
  left: calc(80px + ${props => props.dayIndex} * ((100% - 80px) / 7)); /* Start after time column + position in today's column */
  width: calc((100% - 80px) / 7); /* Width of one day column */
  top: ${props => props.top}px;
  height: 2px;
  background: rgba(133, 105, 143, 0.3);
  z-index: 100;
  pointer-events: none;
  
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: -4px;
    width: 8px;
    height: 8px;
    background: rgba(133, 105, 143, 1.0);
    border-radius: 50%;
    transform: translateX(-50%);
  }
`;

// Dashboard Layout Components
const DashboardContainer = styled.div`
  position: fixed;
  top: 80px; /* Just the header height */
  left: 240px; /* 220px sidebar + 20px padding */
  right: 20px;
  bottom: 20px;
  display: flex;
  flex-direction: column;
  background: #f8f9fc;
  overflow: hidden;
  box-sizing: border-box;
  gap: 16px;
  min-width: 0; /* Allow shrinking */
  
  @media (max-width: 1200px) {
    left: 220px; /* 200px sidebar + 20px padding */
    gap: 12px;
  }
  
  @media (max-width: 768px) {
    top: 70px; /* Just the header height */
    left: 20px; /* No sidebar on mobile */
    gap: 8px;
  }
  
  @media (max-width: 480px) {
    left: 10px;
    right: 10px;
    gap: 6px;
  }
`;

const DashboardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: transparent;
  padding: 45px 0 4px 0;
  margin-left: 300px; /* Align with sidebar width */
  flex-shrink: 0;
  height: 44px;
  
  @media (max-width: 1200px) {
    margin-left: 280px;
  }
  
  @media (max-width: 768px) {
    margin-left: 0;
    padding: 16px 20px 4px 20px;
  }
`;

const HeaderTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const PlannerTitle = styled.h1`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${theme.colors.text};
  margin: 0;
`;

const WeekNavigation = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const WeekTitle = styled.h2`
  font-size: 1.1rem;
  font-weight: 500;
  color: ${theme.colors.text};
  margin: 0;
  min-width: 200px;
  text-align: center;
`;

const DashboardContent = styled.div`
  display: flex;
  gap: 16px;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  
  @media (max-width: 1200px) {
    gap: 12px;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 8px;
  }
`;

const Sidebar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 300px;
  flex-shrink: 0;
  overflow: hidden;
  
  @media (max-width: 1200px) {
    width: 280px;
  }
  
  @media (max-width: 768px) {
    width: 100%;
    flex-direction: row;
    overflow-x: auto;
  }
`;

const SidebarCard = styled.div`
  background: white;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  border: 1px solid #e1e5e9;
  display: flex;
  flex-direction: column;
  
  &:first-child {
    /* Mini Calendar */
    flex-shrink: 0;
    height: 300px;
    overflow: hidden;
  }
  
  &:nth-child(2) {
    /* Member Filters */
    flex-shrink: 0;
    height: 250px; /* Increased from 150px to 250px */
    overflow-y: auto;
    
    /* Hide scrollbar for Member filters panel */
    &::-webkit-scrollbar {
      display: none;
    }
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
  
  &:last-child {
    /* Priorities */
    flex-shrink: 0;
    height: 250px; /* Same size as filter panel */
    min-height: 200px;
    overflow-y: auto;
  }
  
  @media (max-width: 768px) {
    &:first-child {
      width: 280px;
      height: 200px;
    }
    
    &:nth-child(2) {
      width: 200px;
      height: 180px; /* Increased for mobile */
    }
    
    &:last-child {
      width: 200px;
      height: 180px; /* Same size as filter panel on mobile */
    }
  }
`;

const SidebarTitle = styled.h3`
  font-size: 0.9rem;
  font-weight: 600;
  color: ${theme.colors.text};
  margin: 0 0 12px 0;
`;

const SidebarNavigation = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 16px;
`;

const WeekNavigationRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const WeekTitleSidebar = styled.h2`
  font-size: 1rem;
  font-weight: 600;
  color: ${theme.colors.text};
  margin: 0;
  text-align: center;
  flex: 1;
`;

const ActionButtonsRow = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`;

const MiniCalendar = styled.div`
  .calendar-grid {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 2px;
    margin-top: 12px;
  }
  
  .day-header {
    text-align: center;
    font-size: 0.7rem;
    font-weight: 500;
    color: ${theme.colors.lightText};
    padding: 6px 2px;
  }
  
  .day-cell {
    aspect-ratio: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.75rem;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s ease;
    min-height: 28px;
    
    &:hover {
      background: ${theme.colors.primary}10;
    }
    
    &.today {
      background: ${theme.colors.primary};
      color: white;
      font-weight: 600;
    }
    
    &.other-month {
      color: ${theme.colors.lightText};
      opacity: 0.5;
    }
    
    &.selected {
      background: ${theme.colors.primary}20;
      color: ${theme.colors.primary};
      font-weight: 600;
    }
    
    &.viewing-week {
      background: ${theme.colors.primary}08;
      border: 1px solid ${theme.colors.primary}20;
    }
    
    &.viewing-week.today {
      background: ${theme.colors.primary};
      color: white;
      font-weight: 600;
      border: 1px solid ${theme.colors.primary};
    }
    
    &.viewing-week.selected {
      background: ${theme.colors.primary}30;
      color: ${theme.colors.primary};
      font-weight: 600;
      border: 1px solid ${theme.colors.primary};
    }
  }
`;

const FilterSection = styled.div`
  .filter-group {
    margin-bottom: 16px;
    
    &:last-child {
      margin-bottom: 0;
    }
  }
  
  .filter-header {
    margin-bottom: 12px;
    display: flex;
    justify-content: flex-end;
    align-items: center;
  }
  
  .toggle-all-btn {
    font-size: 0.75rem;
    color: ${theme.colors.primary};
    cursor: pointer;
    padding: 4px 8px;
    border-radius: 4px;
    transition: all 0.2s ease;
    font-weight: 500;
    
    &:hover {
      background: ${theme.colors.primary}10;
    }
  }
  
  .filter-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 6px;
    cursor: pointer;
    border-radius: 6px;
    transition: all 0.2s ease;
    user-select: none;
    
    &:hover {
      background: ${theme.colors.primary}08;
    }
    
    &.selected {
      background: ${theme.colors.primary}12;
    }
    
    &.unselected {
      opacity: 0.5;
    }
  }
  
  .filter-checkbox {
    width: 16px;
    height: 16px;
    border: 2px solid ${theme.colors.lightText};
    border-radius: 3px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    
    &.checked {
      background: ${theme.colors.primary};
      border-color: ${theme.colors.primary};
      
      &::after {
        content: '✓';
        color: white;
        font-size: 10px;
        font-weight: bold;
      }
    }
  }
  
  .filter-color {
    width: 12px;
    height: 12px;
    border-radius: 3px;
    flex-shrink: 0;
  }
  
  .filter-text {
    font-size: 0.8rem;
    color: ${theme.colors.text};
    flex: 1;
  }
  
  .filter-count {
    font-size: 0.75rem;
    color: ${theme.colors.lightText};
    margin-left: auto;
  }
`;

const PrioritiesSection = styled.div`
  .priority-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 0;
    border-bottom: 1px solid #f0f0f0;
    
    &:last-child {
      border-bottom: none;
    }
  }
  
  .priority-icon {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
  }
  
  .priority-content {
    flex: 1;
    min-width: 0;
  }
  
  .priority-title {
    font-size: 0.85rem;
    font-weight: 500;
    color: ${theme.colors.text};
    margin: 0 0 2px 0;
  }
  
  .priority-method {
    font-size: 0.75rem;
    color: ${theme.colors.lightText};
    margin: 0;
  }
`;

const MainContent = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  border: 1px solid #e1e5e9;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  
  @media (max-width: 768px) {
    height: 400px;
    flex-shrink: 0;
  }
`;

const WeekViewContainer = styled.div`
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const EmptyFilterState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: ${theme.colors.lightText};
  text-align: center;
  padding: 40px 20px;
  
  .empty-icon {
    width: 48px;
    height: 48px;
    margin-bottom: 16px;
    color: ${theme.colors.accent};
    opacity: 0.6;
  }
  
  .empty-title {
    font-size: 1.1rem;
    font-weight: 500;
    margin-bottom: 8px;
    color: ${theme.colors.text};
  }
  
  .empty-subtitle {
    font-size: 0.9rem;
    opacity: 0.7;
  }
`;

const WeekHeader = styled.div`
  display: grid;
  grid-template-columns: 80px repeat(7, 1fr);
  border-bottom: none;
  background: white;
  flex-shrink: 0;
  height: 60px;
`;

const TimeColumnHeader = styled.div`
  padding: 16px 8px;
  border-right: none;
  background: white;
`;

const DayHeader = styled.div<{ isToday?: boolean }>`
  padding: 12px 8px;
  text-align: center;
  border-right: none;
  background: ${props => props.isToday ? theme.colors.primary + '08' : 'white'};
  display: flex;
  flex-direction: column;
  justify-content: center;
  
  &:last-child {
    border-right: none;
  }
  
  .day-name {
    font-size: 0.7rem;
    font-weight: 500;
    color: ${props => props.isToday ? theme.colors.primary : theme.colors.lightText};
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 2px;
  }
  
  .day-number {
    font-size: 1rem;
    font-weight: ${props => props.isToday ? '700' : '600'};
    color: ${props => props.isToday ? theme.colors.primary : theme.colors.text};
  }
`;

const WeekGrid = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: auto; /* Allow horizontal scrolling when needed */
  display: grid;
  grid-template-columns: 80px repeat(7, 1fr);
  min-height: 0;
  min-width: min-content; /* Allow grid to maintain minimum sizes */
  transition: opacity 0.3s ease;
  
  /* Hide scrollbar for Chrome, Safari and Opera */
  &::-webkit-scrollbar {
    display: none;
  }
  
  /* Hide scrollbar for IE, Edge and Firefox */
  -ms-overflow-style: none;  /* IE and Edge */
  scrollbar-width: none;  /* Firefox */
  
  /* Mobile responsive adjustments */
  @media (max-width: 768px) {
    grid-template-columns: 60px repeat(7, 1fr);
    min-width: 650px; /* Increased minimum width to accommodate wider columns */
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 50px repeat(7, 1fr);
    min-width: 570px; /* Increased minimum width for better text display */
  }
`;

const TimeSlot = styled.div`
  height: 80px;
  border-right: none;
  border-bottom: none;
  padding: 8px;
  font-size: 0.75rem;
  color: ${theme.colors.lightText};
  display: flex;
  align-items: flex-start;
  justify-content: center;
  background: white;
  min-width: 0; /* Allow shrinking */
  
  @media (max-width: 768px) {
    padding: 4px;
    font-size: 0.7rem;
  }
  
  @media (max-width: 480px) {
    padding: 2px;
    font-size: 0.65rem;
  }
`;

const DayColumn = styled.div<{ isToday?: boolean; isPast?: boolean }>`
  height: 80px;
  border-right: 1px solid #e1e5e9;
  border-bottom: 1px solid #f0f0f0;
  position: relative;
  background: ${props => {
    if (props.isPast) return '#f8f9fa';
    return 'white';
  }};
  opacity: ${props => props.isPast ? 0.7 : 1};
  filter: ${props => props.isPast ? 'grayscale(20%)' : 'none'};
  overflow: visible; /* Allow text to be fully visible */
  min-width: 80px; /* Increased minimum width for better text readability */
  
  &:last-child {
    border-right: none;
  }
  
  &:hover {
    background: ${props => {
      if (props.isPast) return '#f8f9fa';
      return '#f8f9fc';
    }};
  }
  
  /* Mobile responsive */
  @media (max-width: 768px) {
    min-width: 70px;
  }
  
  @media (max-width: 480px) {
    min-width: 60px;
  }
`;

const TaskSection = styled.div`
  margin-bottom: ${theme.spacing.xl};
`;

const TaskSectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.md};
  padding-bottom: ${theme.spacing.sm};
  border-bottom: 2px solid #f0f0f0;
`;

const TaskSectionIcon = styled.div<{ type: 'chore' | 'lunchbox' | 'dinner' }>`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => {
    const colors = {
      chore: theme.colors.accent,
      lunchbox: theme.colors.secondary,
      dinner: theme.colors.primary
    };
    return colors[props.type] + '20';
  }};
  
  svg {
    width: 18px;
    height: 18px;
    color: ${props => {
      const colors = {
        chore: theme.colors.accent,
        lunchbox: theme.colors.secondary,
        dinner: theme.colors.primary
      };
      return colors[props.type];
    }};
  }
`;

const TaskSectionTitle = styled.h3<{ type: 'chore' | 'lunchbox' | 'dinner' }>`
  color: ${props => {
    const colors = {
      chore: theme.colors.accent,
      lunchbox: theme.colors.secondary,
      dinner: theme.colors.primary
    };
    return colors[props.type];
  }};
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0;
`;

const TaskItem = styled.div<{ completed?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  padding: ${theme.spacing.md};
  border-radius: 8px;
  background: ${props => props.completed ? theme.colors.primary + '10' : '#f8f9fa'};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.completed ? theme.colors.primary + '20' : '#f0f1f3'};
    transform: translateY(-1px);
  }
`;

const TaskCheckbox = styled.div<{ completed?: boolean }>`
  width: 28px;
  height: 28px;
  border: 2px solid ${props => props.completed ? theme.colors.primary : '#ddd'};
  border-radius: 6px;
  background: ${props => props.completed ? theme.colors.primary : 'transparent'};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  
  &:hover {
    transform: scale(1.1);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
    border-color: ${props => props.completed ? theme.colors.primary : theme.colors.primary};
  }
  
  svg {
    width: 16px;
    height: 16px;
    color: white;
    opacity: ${props => props.completed ? 1 : 0};
    transition: opacity 0.2s ease;
  }
`;

const TaskText = styled.span<{ completed?: boolean }>`
  flex: 1;
  color: ${props => props.completed ? theme.colors.primary : theme.colors.text};
  text-decoration: ${props => props.completed ? 'line-through' : 'none'};
  font-size: 1rem;
  line-height: 1.4;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  text-align: center;
  color: ${theme.colors.lightText};
  padding: ${theme.spacing.xxl};
`;

const EmptyStateTitle = styled.h3`
  color: ${theme.colors.primary};
  font-size: 1.5rem;
  margin-bottom: ${theme.spacing.md};
`;

const EmptyStateText = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  margin-bottom: ${theme.spacing.xl};
  max-width: 400px;
`;

const Planner: React.FC<PlannerProps> = ({ profileData }) => {
  const [user] = useAuthState(auth);
  const [currentPlanner, setCurrentPlanner] = useState<WeeklyPlanner | null>(null);
  const [plannerSettings, setPlannerSettings] = useState<PlannerSettings>({
    autoGenerate: false,
    frequency: 'weekly'
  });
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  // Removed viewMode - only using dashboard view now
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [currentTime, setCurrentTime] = useState<Date>(new Date());

  // Add state for member filtering - initially empty, will be populated in useEffect
  const [selectedMembers, setSelectedMembers] = useState<Set<string>>(new Set());
  
  // Track if this is the initial load to control auto-scroll behavior
  const [hasInitiallyScrolled, setHasInitiallyScrolled] = useState(false);
  
  // Task editing popup state
  const [editingTask, setEditingTask] = useState<{
    date: string;
    taskIndex: number;
    task: TimeSlot;
  } | null>(null);

  // Mobile detection state
  const [isMobile, setIsMobile] = useState(false);

  // Real-time sync state
  const [realtimeUnsubscribe, setRealtimeUnsubscribe] = useState<(() => void) | null>(null);

  // Mobile detection effect
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth <= parseInt(theme.breakpoints.mobile));
    };
    
    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  // Reset scroll flag on component mount (page refresh)
  useEffect(() => {
    setHasInitiallyScrolled(false);
  }, []);

  // Update current time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every minute

    return () => clearInterval(timer);
  }, []);

  // Auto-scroll to current time only on initial page load when current time line is not visible
  useEffect(() => {
    const scrollToCurrentTime = () => {
      // Only auto-scroll if:
      // 1. This is the initial load (hasn't scrolled yet)
      // 2. We're in week view (main view)
      // 3. Current planner is loaded
      // 4. Current time line is not visible in viewport
      if (!hasInitiallyScrolled && currentPlanner) {
        setTimeout(() => {
          // Check if current time line is already visible
          if (isCurrentTimeLineInViewport()) {
            setHasInitiallyScrolled(true);
            return;
          }
          
          const currentHour = new Date().getHours();
          const currentMinutes = new Date().getMinutes();
          const scrollContainer = document.querySelector('.week-grid-container') as HTMLElement;
          
          if (!scrollContainer) return;
          
          let scrollPosition = 0;
          
          if (currentHour >= 6 && currentHour <= 22) {
            // Calculate exact position including minutes for current time
            const startHour = 5; // Dashboard starts at 5 AM
            const hoursFromStart = currentHour - startHour;
            const minutesFraction = currentMinutes / 60;
            const totalHoursFromStart = hoursFromStart + minutesFraction;
            const exactPosition = totalHoursFromStart * 80; // 80px per hour
            
            // Center the current time in the viewport
            const containerHeight = scrollContainer.clientHeight;
            const centerOffset = containerHeight / 2;
            scrollPosition = exactPosition - centerOffset;
          } else {
            // Default to 9 AM at the top of the viewport (4 hours from 5 AM start)
            scrollPosition = 4 * 80; // 9 AM is 4 hours from 5 AM start
          }
          
          scrollContainer.scrollTo({
            top: Math.max(0, scrollPosition),
            behavior: 'smooth'
          });
          
          setHasInitiallyScrolled(true);
        }, 500); // Delay to ensure DOM is fully updated
      }
    };

    scrollToCurrentTime();
  }, [currentPlanner, hasInitiallyScrolled]); // Only trigger when these change

  // Initialize selected members when planner data loads
  useEffect(() => {
    if (currentPlanner) {
      const assignedMembers = getAssignedMembers();
      const allMemberNames = assignedMembers.map((member: any) => member.name);
      setSelectedMembers(new Set([...allMemberNames, 'Unassigned']));
    }
  }, [currentPlanner]);

  // Helper function to calculate current time line position
  const getCurrentTimePosition = () => {
    const now = currentTime;
    const hours = now.getHours();
    const minutes = now.getMinutes();
    const totalMinutes = hours * 60 + minutes;
    return totalMinutes; // This will be used to calculate pixel position
  };

  const getCurrentTimeLineTop = () => {
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinutes = now.getMinutes();
    
    // Dashboard week view starts at 5 AM (hour 5)
    const startHour = 5;
    
    // Calculate position relative to 5 AM start
    const hoursFromStart = currentHour - startHour;
    const minutesFraction = currentMinutes / 60;
    const totalHoursFromStart = hoursFromStart + minutesFraction;
    
    // Each hour is 80px in the dashboard view
    return totalHoursFromStart * 80;
  };

  const isCurrentTimeVisible = () => {
    const weekDays = getWeekDays(currentDate);
    return weekDays.some(day => isToday(day));
  };

  const isCurrentTimeLineInViewport = () => {
    if (!isCurrentTimeVisible()) return false;
    
    const scrollContainer = document.querySelector('.week-grid-container') as HTMLElement;
    if (!scrollContainer) return false;
    
    const currentHour = new Date().getHours();
    const currentMinutes = new Date().getMinutes();
    
    // Dashboard week view starts at 5 AM (hour 5)
    const startHour = 5;
    const hoursFromStart = currentHour - startHour;
    const minutesFraction = currentMinutes / 60;
    const totalHoursFromStart = hoursFromStart + minutesFraction;
    const currentTimePosition = totalHoursFromStart * 80; // 80px per hour
    
    const scrollTop = scrollContainer.scrollTop;
    const containerHeight = scrollContainer.clientHeight;
    const viewportTop = scrollTop;
    const viewportBottom = scrollTop + containerHeight;
    
    // Check if current time line is within the visible viewport
    return currentTimePosition >= viewportTop && currentTimePosition <= viewportBottom;
  };

  // Helper functions
  function getWeekStart(date: Date): Date {
    const d = new Date(date);
    const day = d.getDay();
    const diff = day; // Days to go back to Sunday (0)
    const result = new Date(d);
    result.setDate(d.getDate() - diff);
    return result;
  }

  // Removed formatDate - not used in dashboard view

  function isToday(date: Date): boolean {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  }

  function isPast(date: Date): boolean {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day
    const compareDate = new Date(date);
    compareDate.setHours(0, 0, 0, 0); // Reset time to start of day
    return compareDate < today;
  }

  function isSameMonth(date1: Date, date2: Date): boolean {
    return date1.getMonth() === date2.getMonth() && date1.getFullYear() === date2.getFullYear();
  }

  function isCurrentWeek(date: Date): boolean {
    const today = new Date();
    const currentWeekStart = getWeekStart(today);
    const viewWeekStart = getWeekStart(date);
    return currentWeekStart.toDateString() === viewWeekStart.toDateString();
  }

  function getMonthDays(date: Date): Date[] {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());
    
    const days: Date[] = [];
    const currentDate = new Date(startDate);
    
    for (let i = 0; i < 42; i++) {
      days.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return days;
  }

  function getWeekDays(date: Date): Date[] {
    const weekStart = getWeekStart(date);
    const days: Date[] = [];
    
    for (let i = 0; i < 7; i++) {
      const day = new Date(weekStart);
      day.setDate(day.getDate() + i);
      days.push(day);
    }
    
    return days;
  }

  // Load planner data
  useEffect(() => {
    if (profileData) {
      loadPlannerData();
    }
  }, [currentDate, profileData]);

  // Listen for profile updates to refresh planner data
  useEffect(() => {
    const handleProfileUpdate = async () => {
      console.log('Profile updated, refreshing planner...');
      console.log('Current planner exists:', !!currentPlanner);
      console.log('Profile data available:', !!profileData);
      
      // Always regenerate the planner when profile is updated to reflect new preferences
      if (profileData && Object.keys(profileData).length > 0) {
        console.log('Regenerating planner with updated profile data...');
        await generatePlanner();
      } else {
        console.log('Loading planner data...');
        loadPlannerData();
      }
    };

    window.addEventListener('profileUpdated', handleProfileUpdate);
    return () => {
      window.removeEventListener('profileUpdated', handleProfileUpdate);
    };
  }, [profileData]); // Include profileData in dependencies

  const loadPlannerData = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const profileDoc = await getDoc(doc(db, 'userProfiles', user.uid));
      if (profileDoc.exists()) {
        const profileData = profileDoc.data();
        if (profileData.plannerSettings) {
          setPlannerSettings(profileData.plannerSettings);
        }
      }

      await loadWeekPlanner(getWeekStart(currentDate));
    } catch (error) {
      console.error('Error loading planner data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Set up real-time listener for planner changes
  const setupRealtimeListener = (plannerId: string) => {
    // Clean up any existing listener
    if (realtimeUnsubscribe) {
      realtimeUnsubscribe();
    }

    console.log('🔄 Setting up real-time listener for planner:', plannerId);
    
    const plannerRef = doc(db, 'planners', plannerId);
    const unsubscribe = onSnapshot(plannerRef, (doc) => {
      if (doc.exists()) {
        const plannerData = doc.data() as WeeklyPlanner;
        console.log('📱 Real-time update received:', plannerData);
        setCurrentPlanner(plannerData);
      } else {
        console.log('📱 Planner document deleted');
        setCurrentPlanner(null);
      }
    }, (error) => {
      console.error('❌ Real-time listener error:', error);
    });

    setRealtimeUnsubscribe(() => unsubscribe);
  };

  // Clean up real-time listener on component unmount or user change
  useEffect(() => {
    return () => {
      if (realtimeUnsubscribe) {
        console.log('🧹 Cleaning up real-time listener');
        realtimeUnsubscribe();
      }
    };
  }, [realtimeUnsubscribe]);

  const loadWeekPlanner = async (weekStart: Date) => {
    if (!user) return;

    const weekNumber = getWeekNumber(weekStart);
    const year = weekStart.getFullYear();
    const plannerId = `${user.uid}_week_${weekNumber}_${year}`;

    try {
      const plannerDoc = await getDoc(doc(db, 'planners', plannerId));
      if (plannerDoc.exists()) {
        setCurrentPlanner(plannerDoc.data() as WeeklyPlanner);
        
        // Set up real-time listener for this planner
        setupRealtimeListener(plannerId);
      } else {
        setCurrentPlanner(null);
        
        // Check if auto-generation should happen
        await checkAndAutoGeneratePlanner(weekStart);
      }
    } catch (error) {
      console.error('Error loading week planner:', error);
      setCurrentPlanner(null);
    }
  };

  const checkAndAutoGeneratePlanner = async (weekStart: Date) => {
    if (!user || !profileData || !plannerSettings.autoGenerate) return;

    const today = new Date();
    const currentWeekStart = getWeekStart(today);
    
    // Check if we're looking at the current week or future weeks
    const isCurrentOrFutureWeek = weekStart >= currentWeekStart;
    
    if (!isCurrentOrFutureWeek) {
      console.log('Not auto-generating for past weeks');
      return;
    }

    const todayDay = today.getDay(); // 0 = Sunday
    const lastSunday = new Date(today);
    lastSunday.setDate(today.getDate() - todayDay);
    
    // Check if it's Sunday morning (between 6 AM and 12 PM)
    const isSundayMorning = todayDay === 0 && today.getHours() >= 6 && today.getHours() < 12;
    
    // Check if we've passed a Sunday since last generation
    const missedSunday = plannerSettings.lastGenerated && 
                        new Date(plannerSettings.lastGenerated) < lastSunday;
    
    // Check if there's no planner for the current week (this covers the main use case)
    const isCurrentWeek = weekStart.getTime() === currentWeekStart.getTime();
    const noCurrentWeekPlanner = isCurrentWeek; // This function is only called when no planner exists
    
    // Auto-generate in these scenarios:
    // 1. It's Sunday morning (6 AM - 12 PM)
    // 2. We missed a Sunday since last generation
    // 3. There's no planner for the current week (most common case)
    const shouldAutoGenerate = 
      isSundayMorning || 
      missedSunday || 
      noCurrentWeekPlanner;

    if (shouldAutoGenerate) {
      const reason = isSundayMorning ? 'Sunday morning detected' : 
                    missedSunday ? 'Past Sunday missed' : 
                    'No planner exists for current week';
      
      console.log(`Auto-generating planner: ${reason}`);
      try {
        await generatePlanner(weekStart);
        
        // Update last generated date
        await updatePlannerSettings({
          ...plannerSettings,
          lastGenerated: today.toISOString()
        });
      } catch (error) {
        console.error('Error during auto-generation:', error);
      }
    } else {
      console.log('Auto-generation not needed at this time');
    }
  };

  const updatePlannerSettings = async (newSettings: PlannerSettings) => {
    if (!user) return;
    
    try {
      const userProfileRef = doc(db, 'userProfiles', user.uid);
      await updateDoc(userProfileRef, {
        plannerSettings: newSettings
      });
      setPlannerSettings(newSettings);
    } catch (error) {
      console.error('Error updating planner settings:', error);
    }
  };

  function getWeekNumber(date: Date): number {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  }



  const generatePlanner = async (targetWeekStart?: Date | React.MouseEvent) => {
    if (!user) return;
    
    // Generate planner using the provided profile data
    console.log('Generate planner clicked!');
    console.log('Profile data:', profileData);
    console.log('Profile data keys:', Object.keys(profileData || {}));
    console.log('Chores data:', profileData?.chores);
    console.log('Food data:', profileData?.food);
    console.log('Meals data:', profileData?.meals);
    console.log('MessTestData:', profileData?.messTestData);
    console.log('TasteTestData:', profileData?.tasteTestData);
    
    setLoading(true);
    try {
      // Check if it's Sunday or if user manually triggers generation
      const today = new Date();
      const isSunday = today.getDay() === 0;
      
      // Use the profile data passed as prop instead of loading from Firestore again
      if (!profileData || Object.keys(profileData).length === 0) {
        console.error('Profile data not available:', profileData);
        alert('Profile data is not available. Please complete your profile setup first.');
        setLoading(false);
        return;
      }
      
      console.log('✅ Profile data is available, proceeding with generation...');
      
      // Determine which week to generate for
      const isDateParameter = targetWeekStart instanceof Date;
      const targetDate = isDateParameter ? targetWeekStart : currentDate;
      
      // Generate planner using profile-based system
      const plannerWeekStart = getWeekStart(targetDate);
      const plannerWeekEnd = new Date(plannerWeekStart);
      plannerWeekEnd.setDate(plannerWeekStart.getDate() + 6);
      
      const weekNumber = getWeekNumber(plannerWeekStart);
      const year = plannerWeekStart.getFullYear();
      const plannerId = `${user.uid}_week_${weekNumber}_${year}`;

      console.log(`Generating planner for week ${weekNumber}, ${year} (${plannerWeekStart.toDateString()} - ${plannerWeekEnd.toDateString()})`);

      // Create planner structure with intelligent profile-based tasks
      const days: DayPlanner[] = [];
      
      console.log('Starting day generation loop...');
      
      for (let i = 0; i < 7; i++) {
        let dayOfWeek = '';
        let dateString = '';
        
        try {
          console.log(`Generating day ${i + 1}/7...`);
          
          const currentDate = new Date(plannerWeekStart);
          currentDate.setDate(currentDate.getDate() + i);
          dateString = currentDate.toISOString().split('T')[0];
          dayOfWeek = currentDate.toLocaleDateString('en-US', { weekday: 'long' });
          
          console.log(`Processing ${dayOfWeek} (${dateString})`);
          
          // Generate tasks based on comprehensive profile data
          const dayIndex = currentDate.getDay(); // 0 = Sunday, 1 = Monday, etc.
          const household = profileData.household || {};
          const members = household.members || [];
          const householdMemberColors = profileData.householdMemberColors || {};
          const messTestData = profileData.messTestData || {};
          const nestTestData = profileData.nestTestData || {};
          const tasteTestData = profileData.tasteTestData || {};
          
          console.log(`Day ${i + 1} - Members: ${members.length}, MessTest: ${!!messTestData}, TasteTest: ${!!tasteTestData}`);
          
          // Extract profile data for this day's planning
          
          // Check if this is a no-chore day
          console.log(`Checking ${dayOfWeek}: noChoresDays =`, messTestData.noChoresDays, 'productiveDays =', messTestData.productiveDays);
          const isNoChoreDay = messTestData.noChoresDays?.includes(dayOfWeek) || false;
          const isProductiveDay = messTestData.productiveDays?.includes(dayOfWeek) || false;
          console.log(`${dayOfWeek}: isNoChoreDay = ${isNoChoreDay}, isProductiveDay = ${isProductiveDay}`);
          
          // Use the actual no-chore day logic
          const finalIsNoChoreDay = isNoChoreDay;
          
          // Apply no-chore day logic based on profile preferences
          
          // Generate intelligent chore assignments
          let choreTask = '';
          let choreAssignedTo = 'Unassigned';
          let choreColor = '#6B7280';
          
          if (finalIsNoChoreDay) {
            // NO CHORES ON NO-CHORE DAYS - truly rest day
            choreTask = 'Rest day - no chores!';
            choreAssignedTo = 'Everyone';
            choreColor = '#10B981'; // Green for rest day
          } else if (members.length > 0) {
            // Get member-specific chore assignments from profile
            const choreAssignments = messTestData.choreAssignments || [];
            
            // Find available members for this day based on home times
            // First try to find members available during typical chore times (9:00-11:00 AM)
            console.log(`${dayOfWeek}: Checking home times for ${members.length} members`);
            nestTestData.homeTimes?.forEach((ht: any) => {
              console.log(`${dayOfWeek}: ${ht.member} home time: ${ht.homeFrom || 'no start'} - ${ht.homeTo || 'no end'}`);
            });
            
            let availableMembers = members.filter((member: any) => {
              const homeTime = nestTestData.homeTimes?.find((ht: any) => ht.member === member.name);
              if (!homeTime || (!homeTime.homeFrom && !homeTime.homeTo)) {
                console.log(`${dayOfWeek}: ${member.name} - no home times set, assuming available`);
                return true; // Assume available if no specific times set
              }
              
              console.log(`${dayOfWeek}: ${member.name} - checking home time: ${homeTime.homeFrom} to ${homeTime.homeTo}`);
              
              // Check if member is available during typical chore times (9:00-11:00 AM)
              if (homeTime.homeFrom && homeTime.homeTo) {
                const homeFromHour = parseInt(homeTime.homeFrom.split(':')[0]);
                const homeToHour = parseInt(homeTime.homeTo.split(':')[0]);
                console.log(`${dayOfWeek}: ${member.name} - parsed hours: ${homeFromHour} to ${homeToHour}, need 9-11 AM availability`);
                // Available for morning chores if home during 9-11 AM
                const isAvailable = homeFromHour <= 9 && homeToHour >= 11;
                console.log(`${dayOfWeek}: ${member.name} - ${isAvailable ? 'AVAILABLE' : 'NOT AVAILABLE'} for morning chores`);
                return isAvailable;
              } else if (homeTime.homeFrom) {
                // Available from a certain time - check if available by 9 AM
                const homeFromHour = parseInt(homeTime.homeFrom.split(':')[0]);
                console.log(`${dayOfWeek}: ${member.name} - available from ${homeFromHour}:00, need by 9 AM`);
                const isAvailable = homeFromHour <= 9;
                console.log(`${dayOfWeek}: ${member.name} - ${isAvailable ? 'AVAILABLE' : 'NOT AVAILABLE'} for morning chores`);
                return isAvailable;
              } else if (homeTime.homeTo) {
                // Available until a certain time - check if available until 11 AM
                const homeToHour = parseInt(homeTime.homeTo.split(':')[0]);
                console.log(`${dayOfWeek}: ${member.name} - available until ${homeToHour}:00, need until 11 AM`);
                const isAvailable = homeToHour >= 11;
                console.log(`${dayOfWeek}: ${member.name} - ${isAvailable ? 'AVAILABLE' : 'NOT AVAILABLE'} for morning chores`);
                return isAvailable;
              }
              return true;
            });

            // If no members are available during optimal chore times, use ONLY those with chore assignments
            if (availableMembers.length === 0) {
              console.log(`${dayOfWeek}: No members available during optimal chore times, checking for members with chore assignments regardless of home times`);
              availableMembers = members.filter((member: any) => {
                const assignment = choreAssignments.find((ca: any) => ca.member === member.name);
                return assignment && assignment.chores && assignment.chores.length > 0;
              });
              
              if (availableMembers.length === 0) {
                console.log(`${dayOfWeek}: No members have any chore assignments - no chores will be assigned`);
              } else {
                console.log(`${dayOfWeek}: Found ${availableMembers.length} members with chore assignments (ignoring home times for today)`);
              }
            }
            
            if (availableMembers.length > 0) {
              console.log(`${dayOfWeek}: Available members for chores:`, availableMembers.map((m: any) => m.name));
              console.log(`${dayOfWeek}: Chore assignments available:`, choreAssignments.map((ca: any) => `${ca.member}: [${ca.chores.join(', ')}]`));
              
              // Rotate through available members, but prefer those with specific chore assignments
              let assignedMember: any = null;
              let memberChores: string[] = [];
              
              // STRICT: Only assign chores to members who have specific chore assignments
              const membersWithNonCookingChores = availableMembers.filter((member: any) => {
                const assignment = choreAssignments.find((ca: any) => ca.member === member.name);
                if (!assignment || !assignment.chores.length) return false;
                
                const nonCookingChores = assignment.chores.filter((chore: string) => 
                  // Exclude cooking-related chores from general chore assignment
                  !chore.toLowerCase().includes('cook') && 
                  !chore.toLowerCase().includes('meal') && 
                  !chore.toLowerCase().includes('lunch') &&
                  !chore.toLowerCase().includes('dinner') &&
                  !chore.toLowerCase().includes('food prep')
                );
                return nonCookingChores.length > 0;
              });
              
              console.log(`${dayOfWeek}: Members with non-cooking chore assignments:`, membersWithNonCookingChores.map((m: any) => m.name));
              
              // Only assign chores if there are members with specific assignments
              if (membersWithNonCookingChores.length > 0) {
                // Rotate through members who actually have chore assignments
                const memberIndex = dayIndex % membersWithNonCookingChores.length;
                assignedMember = membersWithNonCookingChores[memberIndex];
                
                const assignment = choreAssignments.find((ca: any) => ca.member === assignedMember.name);
                memberChores = assignment.chores.filter((chore: string) => 
                  // Exclude cooking-related chores from general chore assignment
                  !chore.toLowerCase().includes('cook') && 
                  !chore.toLowerCase().includes('meal') && 
                  !chore.toLowerCase().includes('lunch') &&
                  !chore.toLowerCase().includes('dinner') &&
                  !chore.toLowerCase().includes('food prep')
                );
                
                console.log(`${dayOfWeek}: Assigned ${assignedMember.name} with their specific chores:`, memberChores);
              } else {
                console.log(`${dayOfWeek}: No members have non-cooking chore assignments - leaving chore unassigned`);
                choreTask = 'No assigned chores today';
                choreAssignedTo = 'Unassigned';
              }
              
              // Only proceed with chore selection if we have an assigned member with chores
              if (assignedMember && memberChores.length > 0) {
                // Select appropriate chore for the day
                if (isProductiveDay && memberChores.length > 1) {
                  // On productive days, assign more challenging or important chores
                  const importantChores = memberChores.filter(chore => 
                    chore.includes('deep') || chore.includes('organize') || chore.includes('bathroom') || chore.includes('vacuum')
                  );
                  choreTask = importantChores.length > 0 
                    ? importantChores[Math.floor(Math.random() * importantChores.length)]
                    : memberChores[Math.floor(Math.random() * memberChores.length)];
                } else {
                  // Regular day - rotate through assigned chores
                  choreTask = memberChores[dayIndex % memberChores.length];
                }
                
                choreAssignedTo = assignedMember.name;
                
                // Get member color
                const memberIndex = members.findIndex((m: any) => m.name === assignedMember.name);
                choreColor = householdMemberColors[memberIndex] || '#6B7280';
                
                console.log(`${dayOfWeek}: Final chore assignment - ${assignedMember.name}: "${choreTask}"`);
              }
            }
          }
          
          // Generate intelligent meal assignments - ONLY assign to members with cooking chores
          let lunchboxMeal = 'Healthy lunch option';
          let lunchboxAssignedTo = 'Unassigned';
          let lunchboxColor = '#6B7280';
          let dinnerMeal = 'Nutritious dinner';
          let dinnerAssignedTo = 'Unassigned';
          let dinnerColor = '#6B7280';
          
          if (members.length > 0) {
            // Find members who can cook based on chore assignments - STRICT FILTERING
            const choreAssignments = messTestData.choreAssignments || [];
            const cookingMembers = members.filter((member: any) => {
              const assignment = choreAssignments.find((ca: any) => ca.member === member.name);
              if (!assignment || !assignment.chores) return false;
              
              // Check if member has ANY cooking-related chores (exclude cleaning tasks)
              return assignment.chores.some((chore: string) => {
                const lowerChore = chore.toLowerCase();
                return (
                  lowerChore.includes('cook') || 
                  lowerChore.includes('meal') || 
                  lowerChore.includes('lunch') ||
                  lowerChore.includes('dinner') ||
                  lowerChore.includes('food prep') ||
                  lowerChore.includes('meal prep')
                ) && !lowerChore.includes('clean'); // Exclude cleaning tasks
              });
            });
            
            console.log(`${dayOfWeek}: Found ${cookingMembers.length} members with cooking chores:`, cookingMembers.map((m: any) => m.name));
            
            // ONLY assign meals to members with cooking chores - if none, leave unassigned
            if (cookingMembers.length > 0) {
              const cookIndex = dayIndex % cookingMembers.length;
              const assignedCookingMember = cookingMembers[cookIndex];
              
              // Check if member is available during meal times
              const homeTime = nestTestData.homeTimes?.find((ht: any) => ht.member === assignedCookingMember.name);
              
              // Simple availability check for lunchbox (8-9 AM)
              let isAvailableForLunchbox = true;
              console.log(`${dayOfWeek}: Checking ${assignedCookingMember.name} availability for lunchbox (8-9 AM)`);
              if (homeTime?.homeFrom && homeTime?.homeTo) {
                const homeFromHour = parseInt(homeTime.homeFrom.split(':')[0]);
                const homeToHour = parseInt(homeTime.homeTo.split(':')[0]);
                console.log(`${dayOfWeek}: ${assignedCookingMember.name} - home ${homeFromHour}:00-${homeToHour}:00, need 8-9 AM`);
                isAvailableForLunchbox = homeFromHour <= 8 && homeToHour >= 9;
                console.log(`${dayOfWeek}: ${assignedCookingMember.name} - lunchbox availability: ${isAvailableForLunchbox}`);
              } else if (homeTime?.homeFrom) {
                const homeFromHour = parseInt(homeTime.homeFrom.split(':')[0]);
                console.log(`${dayOfWeek}: ${assignedCookingMember.name} - home from ${homeFromHour}:00, need by 8 AM`);
                isAvailableForLunchbox = homeFromHour <= 8;
                console.log(`${dayOfWeek}: ${assignedCookingMember.name} - lunchbox availability: ${isAvailableForLunchbox}`);
              } else if (homeTime?.homeTo) {
                const homeToHour = parseInt(homeTime.homeTo.split(':')[0]);
                console.log(`${dayOfWeek}: ${assignedCookingMember.name} - home until ${homeToHour}:00, need until 9 AM`);
                isAvailableForLunchbox = homeToHour >= 9;
                console.log(`${dayOfWeek}: ${assignedCookingMember.name} - lunchbox availability: ${isAvailableForLunchbox}`);
              }
              
              // Simple availability check for dinner (5-7 PM = 17-19 in 24hr)
              let isAvailableForDinner = true;
              console.log(`${dayOfWeek}: Checking ${assignedCookingMember.name} availability for dinner (17-19 / 5-7 PM)`);
              if (homeTime?.homeFrom && homeTime?.homeTo) {
                const homeFromHour = parseInt(homeTime.homeFrom.split(':')[0]);
                const homeToHour = parseInt(homeTime.homeTo.split(':')[0]);
                console.log(`${dayOfWeek}: ${assignedCookingMember.name} - home ${homeFromHour}:00-${homeToHour}:00, need 17-19 (5-7 PM)`);
                isAvailableForDinner = homeFromHour <= 17 && homeToHour >= 19;
                console.log(`${dayOfWeek}: ${assignedCookingMember.name} - dinner availability: ${isAvailableForDinner}`);
              } else if (homeTime?.homeFrom) {
                const homeFromHour = parseInt(homeTime.homeFrom.split(':')[0]);
                console.log(`${dayOfWeek}: ${assignedCookingMember.name} - home from ${homeFromHour}:00, need by 17 (5 PM)`);
                isAvailableForDinner = homeFromHour <= 17;
                console.log(`${dayOfWeek}: ${assignedCookingMember.name} - dinner availability: ${isAvailableForDinner}`);
              } else if (homeTime?.homeTo) {
                const homeToHour = parseInt(homeTime.homeTo.split(':')[0]);
                console.log(`${dayOfWeek}: ${assignedCookingMember.name} - home until ${homeToHour}:00, need until 19 (7 PM)`);
                isAvailableForDinner = homeToHour >= 19;
                console.log(`${dayOfWeek}: ${assignedCookingMember.name} - dinner availability: ${isAvailableForDinner}`);
              }
              
              // Assign meals only if member is available for the respective time slots
              if (isAvailableForLunchbox || isAvailableForDinner) {
                // Get member color
                const originalMemberIndex = members.findIndex((member: any) => member.name === assignedCookingMember.name);
                const memberColor = householdMemberColors[originalMemberIndex] || '#6B7280';
                
                // Assign lunchbox only if available during lunchbox time
                if (isAvailableForLunchbox) {
                  lunchboxAssignedTo = assignedCookingMember.name;
                  lunchboxColor = memberColor;
                }
                
                // Assign dinner only if available during dinner time
                if (isAvailableForDinner) {
                  dinnerAssignedTo = assignedCookingMember.name;
                  dinnerColor = memberColor;
                }
                
                console.log(`${dayOfWeek}: Assigned cooking to ${assignedCookingMember.name} - Lunchbox: ${isAvailableForLunchbox ? 'Yes' : 'No'}, Dinner: ${isAvailableForDinner ? 'Yes' : 'No'}`);
              }
            } else {
              console.log(`${dayOfWeek}: No members with cooking chores found - meals left unassigned`);
            }
          }
          
          // Generate meals based on dietary preferences and taste test data
          const isWeekend = dayIndex === 0 || dayIndex === 6;
          const dietaryRestrictions = tasteTestData.dietaryRestrictions || [];
          const foodPreferences = tasteTestData.foodPreferences || {};
          
          // Lunchbox options based on preferences
          const lunchboxOptions = [
            'Sandwich and fruit',
            'Salad with protein',
            'Leftover dinner portions',
            'Soup and crackers',
            'Wrap with vegetables',
            'Pasta salad',
            'Quinoa bowl'
          ];
          
          // Dinner options based on day and preferences
          const weekdayDinners = [
            'Quick stir-fry with vegetables',
            'Pasta with marinara sauce',
            'Grilled chicken and rice',
            'Tacos with ground turkey',
            'Baked salmon with potatoes'
          ];
          
          const weekendDinners = [
            'Slow-cooked roast dinner',
            'Homemade pizza night',
            'BBQ and grilled vegetables',
            'Family curry with rice',
            'Hearty soup and bread'
          ];
          
          // Apply dietary restrictions
          if (dietaryRestrictions.some((dr: any) => dr.name.toLowerCase().includes('vegetarian'))) {
            lunchboxMeal = 'Vegetarian lunch bowl';
            dinnerMeal = isWeekend ? 'Vegetarian family feast' : 'Plant-based dinner';
          } else if (dietaryRestrictions.some((dr: any) => dr.name.toLowerCase().includes('keto'))) {
            lunchboxMeal = 'Low-carb lunch option';
            dinnerMeal = isWeekend ? 'Keto weekend meal' : 'Low-carb dinner';
          } else {
            lunchboxMeal = lunchboxOptions[Math.floor(Math.random() * lunchboxOptions.length)];
            dinnerMeal = isWeekend 
              ? weekendDinners[Math.floor(Math.random() * weekendDinners.length)]
              : weekdayDinners[Math.floor(Math.random() * weekdayDinners.length)];
          }
          
          // Apply cuisine preferences if available
          if (foodPreferences.cuisines && foodPreferences.cuisines.length > 0) {
            const preferredCuisine = foodPreferences.cuisines[dayIndex % foodPreferences.cuisines.length];
            if (preferredCuisine.toLowerCase().includes('italian')) {
              dinnerMeal = isWeekend ? 'Italian family dinner' : 'Quick Italian pasta';
            } else if (preferredCuisine.toLowerCase().includes('asian')) {
              dinnerMeal = isWeekend ? 'Asian feast night' : 'Asian stir-fry';
            } else if (preferredCuisine.toLowerCase().includes('mexican')) {
              dinnerMeal = isWeekend ? 'Mexican family night' : 'Quick Mexican bowl';
            }
          }
          
          // Generate time-based tasks throughout the day
          const timeSlots: TimeSlot[] = [];

          // Helper function to check if task time overlaps with member's home time
          const isTimeWithinRange = (taskStart: string, taskEnd: string, homeFrom: string, homeTo: string): boolean => {
            // If no home times specified, assume available
            if (!homeFrom && !homeTo) return true;
            
            // Convert time strings to minutes for easier comparison
            const timeToMinutes = (time: string): number => {
              const [hours, minutes] = time.split(':').map(Number);
              return hours * 60 + minutes;
            };
            
            const taskStartMin = timeToMinutes(taskStart);
            const taskEndMin = timeToMinutes(taskEnd);
            
            // If only homeFrom is specified (available from this time onwards)
            if (homeFrom && !homeTo) {
              const homeFromMin = timeToMinutes(homeFrom);
              return taskStartMin >= homeFromMin;
            }
            
            // If only homeTo is specified (available until this time)
            if (!homeFrom && homeTo) {
              const homeToMin = timeToMinutes(homeTo);
              return taskEndMin <= homeToMin;
            }
            
            // If both homeFrom and homeTo are specified (available in this range)
            if (homeFrom && homeTo) {
              const homeFromMin = timeToMinutes(homeFrom);
              const homeToMin = timeToMinutes(homeTo);
              
              // Handle cases where home time might span midnight
              if (homeFromMin <= homeToMin) {
                // Normal case: home from 9:00 to 17:00
                return taskStartMin >= homeFromMin && taskEndMin <= homeToMin;
              } else {
                // Spans midnight: home from 22:00 to 06:00
                return (taskStartMin >= homeFromMin && taskStartMin >= homeToMin) || 
                       (taskEndMin <= homeToMin && taskEndMin <= homeFromMin);
              }
            }
            
            return true; // Default to available if logic doesn't match
          };
          
          // Helper function to get random time in range
          const getRandomTimeInRange = (startHour: number, endHour: number): string => {
            const hour = Math.floor(Math.random() * (endHour - startHour + 1)) + startHour;
            const minute = Math.random() < 0.5 ? 0 : 30; // Either :00 or :30
            return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
          };
          
          // Helper function to get task end time
          const getTaskEndTime = (startTime: string, taskType: string): string => {
            const [hour, minute] = startTime.split(':').map(Number);
            let duration = 30; // Default 30 minutes
            
            switch (taskType) {
              case 'chore':
                duration = 45; // 45 minutes for chores
                break;
              case 'lunchbox':
                duration = 20; // 20 minutes for lunchbox prep
                break;
              case 'dinner':
                duration = 60; // 1 hour for dinner prep
                break;
              case 'task':
                duration = 15; // 15 minutes for quick tasks like bed making
                break;
            }
            
            const endMinute = minute + duration;
            const endHour = hour + Math.floor(endMinute / 60);
            const finalMinute = endMinute % 60;
            
            return `${endHour.toString().padStart(2, '0')}:${finalMinute.toString().padStart(2, '0')}`;
          };
          
          // DAILY BED MAKING - Assign to ALL members who have bed-making in their chore assignments
          if (members.length > 0) {
            const choreAssignments = messTestData.choreAssignments || [];
            console.log(`${dayOfWeek}: Checking bed-making assignments for ${members.length} members`);
            
            // Find all members who have bed-making in their chore assignments
            const membersWithBedMaking = members.filter((member: any) => {
              const assignment = choreAssignments.find((ca: any) => ca.member === member.name);
              return assignment?.chores?.some((chore: string) => 
                chore.toLowerCase().includes('bed') || 
                chore.toLowerCase().includes('bedroom') ||
                chore.toLowerCase().includes('make bed') ||
                chore.toLowerCase().includes('tidy bedroom')
              );
            });
            
            // Assign bed-making to EVERY member who has it in their assignments
            if (membersWithBedMaking.length > 0) {
              console.log(`${dayOfWeek}: Found ${membersWithBedMaking.length} members with bed-making assignments`);
              
              membersWithBedMaking.forEach((member: any, index: number) => {
                const memberIndex = members.findIndex((m: any) => m.name === member.name);
                const bedMakingTime = getRandomTimeInRange(7, 7); // 7:00 or 7:30 AM
                const memberColor = householdMemberColors[memberIndex] || '#6B7280';
                
                timeSlots.push({
                  time: bedMakingTime,
                  startTime: bedMakingTime,
                  endTime: getTaskEndTime(bedMakingTime, 'task'),
                  activity: `Make bed`,
                  type: 'task',
                  assignedTo: member.name,
                  assignedColor: memberColor,
                  isCompleted: false
                });
                
                console.log(`${dayOfWeek}: ✅ Assigned bed-making to ${member.name} (daily assignment)`);
              });
            } else {
              console.log(`${dayOfWeek}: ⏭️ No members have bed-making in their chore profiles`);
            }
          }
          
          // Morning lunchbox preparation (8:00-9:00 AM)
          if (lunchboxAssignedTo !== 'Unassigned') {
            const lunchboxTime = getRandomTimeInRange(8, 8); // 8:00 or 8:30
            timeSlots.push({
              time: lunchboxTime,
              startTime: lunchboxTime,
              endTime: getTaskEndTime(lunchboxTime, 'lunchbox'),
              activity: lunchboxMeal,
              type: 'lunchbox',
              assignedTo: lunchboxAssignedTo,
              assignedColor: lunchboxColor,
              isCompleted: false
            });
          }
          
          // Morning chores (9:00-11:00 AM) - only if not a no-chore day
          if (!finalIsNoChoreDay && choreAssignedTo !== 'Unassigned') {
            const morningChoreTime = getRandomTimeInRange(9, 10);
            timeSlots.push({
              time: morningChoreTime,
              startTime: morningChoreTime,
              endTime: getTaskEndTime(morningChoreTime, 'chore'),
              activity: choreTask,
              type: 'chore',
              assignedTo: choreAssignedTo,
              assignedColor: choreColor,
              isCompleted: false
            });
          }
          
          // Additional chores on productive days (2:00-4:00 PM and 7:00-8:00 PM)
          console.log(`${dayOfWeek}: Checking productive day conditions - isProductiveDay=${isProductiveDay}, finalIsNoChoreDay=${finalIsNoChoreDay}, members.length=${members.length}`);
          if (isProductiveDay && !finalIsNoChoreDay && members.length > 0) {
            console.log(`${dayOfWeek}: Adding extra chores for productive day`);
            const choreAssignments = messTestData.choreAssignments || [];
            const availableMembers = members.filter((member: any) => {
              const assignment = choreAssignments.find((ca: any) => ca.member === member.name);
              return assignment && assignment.chores && assignment.chores.length > 0;
            });
            console.log(`${dayOfWeek}: Available members for extra chores:`, availableMembers.map((m: any) => m.name));
            
            if (availableMembers.length > 0) {
              // First additional chore (2:00-3:30 PM) - STRICT: only assign chores from member's assignments
              const afternoonMember = availableMembers[Math.floor(Math.random() * availableMembers.length)];
              const assignment = choreAssignments.find((ca: any) => ca.member === afternoonMember.name);
              if (assignment && assignment.chores && assignment.chores.length > 0) {
                // Filter to only chores they're actually assigned to (excluding cooking and bed-making)
                const availableAfternoonChores = assignment.chores.filter((chore: string) => 
                  !chore.toLowerCase().includes('cook') && 
                  !chore.toLowerCase().includes('meal') && 
                  !chore.toLowerCase().includes('lunch') &&
                  !chore.toLowerCase().includes('dinner') &&
                  !chore.toLowerCase().includes('food prep') &&
                  !chore.toLowerCase().includes('bed') &&
                  !chore.toLowerCase().includes('bedroom') &&
                  !chore.toLowerCase().includes('make bed')
                );
                
                                if (availableAfternoonChores.length > 0) {
                  const additionalChore = availableAfternoonChores[Math.floor(Math.random() * availableAfternoonChores.length)];
                  console.log(`${dayOfWeek}: Adding afternoon productive chore: ${additionalChore} for ${afternoonMember.name} (from their assignments)`);
                  
                  const afternoonChoreTime = getRandomTimeInRange(14, 15); // 2:00-3:30 PM
                  
                  const memberIndex = members.findIndex((m: any) => m.name === afternoonMember.name);
                  const memberColor = householdMemberColors[memberIndex] || '#6B7280';
                  
                  timeSlots.push({
                    time: afternoonChoreTime,
                    startTime: afternoonChoreTime,
                    endTime: getTaskEndTime(afternoonChoreTime, 'chore'),
                    activity: additionalChore,
                    type: 'chore',
                    assignedTo: afternoonMember.name,
                    assignedColor: memberColor,
                    isCompleted: false
                  });
                                } else {
                  console.log(`${dayOfWeek}: ${afternoonMember.name} has no available chores for afternoon productive time`);
                }

                // Second additional chore on productive days (7:00-8:00 PM) - evening organizing
                if (availableMembers.length > 1 || assignment.chores.length > 1) {
                const eveningMember = availableMembers.length > 1 
                  ? availableMembers[Math.floor(Math.random() * availableMembers.length)]
                  : afternoonMember;
                const eveningAssignment = choreAssignments.find((ca: any) => ca.member === eveningMember.name);
                
                // Filter out bed-making and cooking tasks from all chores for evening assignment
                const availableEveningChores = eveningAssignment.chores.filter((chore: string) => 
                  !(chore.toLowerCase().includes('bed') || 
                    chore.toLowerCase().includes('bedroom') ||
                    chore.toLowerCase().includes('make bed') ||
                    chore.toLowerCase().includes('tidy bedroom') ||
                    chore.toLowerCase().includes('cook') || 
                    chore.toLowerCase().includes('meal') || 
                    chore.toLowerCase().includes('lunch') ||
                    chore.toLowerCase().includes('dinner') ||
                    chore.toLowerCase().includes('food prep'))
                );
                
                // ONLY assign if there are actual chores available from the member's assignments
                if (availableEveningChores.length > 0) {
                  const eveningChore = availableEveningChores[Math.floor(Math.random() * availableEveningChores.length)];
                  console.log(`${dayOfWeek}: Adding evening productive chore: ${eveningChore} for ${eveningMember.name} (from their assignments)`);
                  
                  const eveningChoreTime = getRandomTimeInRange(19, 19); // 7:00-7:30 PM
                  const eveningMemberIndex = members.findIndex((m: any) => m.name === eveningMember.name);
                  const eveningMemberColor = householdMemberColors[eveningMemberIndex] || '#6B7280';
                  
                  timeSlots.push({
                    time: eveningChoreTime,
                    startTime: eveningChoreTime,
                    endTime: getTaskEndTime(eveningChoreTime, 'chore'),
                    activity: eveningChore,
                    type: 'chore',
                    assignedTo: eveningMember.name,
                    assignedColor: eveningMemberColor,
                    isCompleted: false
                  });
                } else {
                  console.log(`${dayOfWeek}: ${eveningMember.name} has no available chores for evening productive time - skipping evening assignment`);
                }
                }
              }
            }
          }
          
          // Evening dinner preparation (5:00-7:00 PM)
          if (dinnerAssignedTo !== 'Unassigned') {
            const dinnerTime = getRandomTimeInRange(17, 18); // 5:00-6:30 PM
            timeSlots.push({
              time: dinnerTime,
              startTime: dinnerTime,
              endTime: getTaskEndTime(dinnerTime, 'dinner'),
              activity: dinnerMeal,
              type: 'dinner',
              assignedTo: dinnerAssignedTo,
              assignedColor: dinnerColor,
              isCompleted: false
            });
          }
          
          // Sort time slots by time
          timeSlots.sort((a, b) => a.time.localeCompare(b.time));
          
          // Create the day object with proper validation to prevent undefined values
          const dayData: DayPlanner = {
            date: dateString,
            dayOfWeek: dayOfWeek,
            chore: choreTask || (finalIsNoChoreDay ? 'Rest day - no chores!' : 'No assigned chores today'), // Ensure never undefined
            lunchbox: lunchboxMeal || 'Simple lunch', // Ensure never undefined
            dinner: dinnerMeal || 'Easy dinner', // Ensure never undefined
            timeSlots: timeSlots || [], // Ensure never undefined
            isCompleted: {
              chore: false,
              lunchbox: false,
              dinner: false
            }
          };
          
          // Only add assignment fields if they have actual assignments
          if (choreAssignedTo !== 'Unassigned') {
            dayData.choreAssignedTo = choreAssignedTo;
            dayData.choreColor = choreColor;
          }
          if (lunchboxAssignedTo !== 'Unassigned') {
            dayData.lunchboxAssignedTo = lunchboxAssignedTo;
            dayData.lunchboxColor = lunchboxColor;
          }
          if (dinnerAssignedTo !== 'Unassigned') {
            dayData.dinnerAssignedTo = dinnerAssignedTo;
            dayData.dinnerColor = dinnerColor;
          }
          
          // Validate the day data before adding to prevent Firestore errors
          console.log(`Day ${i + 1} data:`, dayData);
          
          // Check for any undefined values
          const hasUndefinedValues = Object.values(dayData).some(value => 
            value === undefined || 
            (typeof value === 'object' && value !== null && Object.values(value).some(v => v === undefined))
          );
          
          if (hasUndefinedValues) {
            console.warn(`⚠️ Day ${i + 1} has undefined values, fixing...`);
            // Fix any undefined values
            if (!dayData.chore) dayData.chore = 'No assigned chores today';
            if (!dayData.lunchbox) dayData.lunchbox = 'Simple lunch';
            if (!dayData.dinner) dayData.dinner = 'Easy dinner';
            if (!dayData.timeSlots) dayData.timeSlots = [];
          }
          
          days.push(dayData);
          console.log(`✅ Day ${i + 1} (${dayOfWeek}) generated successfully`);
        } catch (error) {
          console.error(`❌ Error generating day ${i + 1} (${dayOfWeek}):`, error);
          // Create a fallback day with minimal data to prevent complete failure
          const fallbackDay: DayPlanner = {
            date: dateString,
            dayOfWeek: dayOfWeek,
            chore: 'No assigned chores today',
            lunchbox: 'Simple lunch',
            dinner: 'Easy dinner',
            timeSlots: [],
            isCompleted: {
              chore: false,
              lunchbox: false,
              dinner: false
            }
          };
          days.push(fallbackDay);
          console.log(`Created fallback day for ${dayOfWeek}`);
        }
      }

      console.log(`✅ Day generation complete. Generated ${days.length} days.`);
      
      if (days.length === 0) {
        throw new Error('No days were generated. Check profile data and try again.');
      }

      // Final cleanup: ensure all days have required fields
      console.log('🧹 Final cleanup of day data...');
      days.forEach((day, index) => {
        // Ensure all required string fields are set
                    if (!day.chore) day.chore = 'No assigned chores today';
        if (!day.lunchbox) day.lunchbox = 'Simple lunch';
        if (!day.dinner) day.dinner = 'Easy dinner';
        if (!day.date) day.date = new Date().toISOString().split('T')[0];
        if (!day.dayOfWeek) day.dayOfWeek = 'Unknown';
        
        // Ensure timeSlots is always an array
        if (!day.timeSlots) day.timeSlots = [];
        
        // Ensure isCompleted object exists with all required fields
        if (!day.isCompleted) {
          day.isCompleted = { chore: false, lunchbox: false, dinner: false };
        } else {
          if (day.isCompleted.chore === undefined) day.isCompleted.chore = false;
          if (day.isCompleted.lunchbox === undefined) day.isCompleted.lunchbox = false;
          if (day.isCompleted.dinner === undefined) day.isCompleted.dinner = false;
        }
        
        // Clean up timeSlots to ensure no undefined values
        day.timeSlots = day.timeSlots.map((slot: any) => ({
          time: slot.time || '09:00',
          startTime: slot.startTime || slot.time || '09:00',
          endTime: slot.endTime || '10:00',
          activity: slot.activity || 'Task',
          type: slot.type || 'task',
          assignedTo: slot.assignedTo || undefined, // Allow undefined for optional fields
          assignedColor: slot.assignedColor || undefined, // Allow undefined for optional fields
          isCompleted: slot.isCompleted !== undefined ? slot.isCompleted : false
        } as TimeSlot));
        
        console.log(`Day ${index + 1} (${day.dayOfWeek}) cleaned up:`, {
          chore: day.chore,
          lunchbox: day.lunchbox,
          dinner: day.dinner,
          timeSlots: day.timeSlots.length
        });
      });

      const newPlanner: WeeklyPlanner = {
        id: plannerId,
        userId: user.uid,
        startDate: plannerWeekStart.toISOString().split('T')[0],
        endDate: plannerWeekEnd.toISOString().split('T')[0],
        weekNumber,
        year,
        days,
        isGenerated: true,
        createdAt: new Date().toISOString()
      };

      // Comprehensive validation to prevent Firestore errors
      console.log('🔍 Validating planner data before saving...');
      
      // Validate main planner object
      const validateObject = (obj: any, path: string = ''): boolean => {
        for (const [key, value] of Object.entries(obj)) {
          const currentPath = path ? `${path}.${key}` : key;
          
          if (value === undefined) {
            console.error(`❌ Found undefined value at ${currentPath}`);
            return false;
          }
          
          if (value === null) {
            console.warn(`⚠️ Found null value at ${currentPath} (this is okay for Firestore)`);
          }
          
          if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
            if (!validateObject(value, currentPath)) {
              return false;
            }
          }
          
          if (Array.isArray(value)) {
            for (let i = 0; i < value.length; i++) {
              const item = value[i];
              if (item === undefined) {
                console.error(`❌ Found undefined value in array at ${currentPath}[${i}]`);
                return false;
              }
              if (typeof item === 'object' && item !== null) {
                if (!validateObject(item, `${currentPath}[${i}]`)) {
                  return false;
                }
              }
            }
          }
        }
        return true;
      };
      
      if (!validateObject(newPlanner)) {
        throw new Error('Planner data contains undefined values. Cannot save to Firestore.');
      }
      
      console.log('✅ Planner data validation passed');
      console.log('💾 Saving planner to Firestore...');
      await setDoc(doc(db, 'planners', plannerId), newPlanner);
      setCurrentPlanner(newPlanner);
      
      // Set up real-time listener for the newly created planner
      setupRealtimeListener(plannerId);
      
      console.log('✅ Planner generated successfully!', newPlanner);
      console.log(`Generated ${days.length} days with ${days.reduce((total, day) => total + (day.timeSlots?.length || 0), 0)} total tasks`);
      
      // Force a re-render by updating the current date slightly
      setCurrentDate(new Date(currentDate));
      
    } catch (error) {
      console.error('❌ Error generating planner:', error);
      console.error('Error details:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined,
        profileDataAvailable: !!profileData,
        userAvailable: !!user
      });
      alert(`Failed to generate planner: ${error instanceof Error ? error.message : 'Unknown error'}. Please check the console for details.`);
    } finally {
      setLoading(false);
    }
  };

  const toggleTaskCompletion = async (date: string, taskType: 'chore' | 'lunchbox' | 'dinner') => {
    if (!currentPlanner || !user) return;

    const dayIndex = currentPlanner.days.findIndex(day => day.date === date);
    if (dayIndex === -1) return;

    const updatedPlanner = { ...currentPlanner };
    updatedPlanner.days[dayIndex].isCompleted[taskType] = !updatedPlanner.days[dayIndex].isCompleted[taskType];

    try {
      await setDoc(doc(db, 'planners', currentPlanner.id), updatedPlanner);
      setCurrentPlanner(updatedPlanner);
    } catch (error) {
      console.error('Error updating task completion:', error);
    }
  };

  const toggleIndividualTaskCompletion = async (date: string, taskActivity: string, taskTime: string) => {
    if (!currentPlanner || !user) return;

    const dayIndex = currentPlanner.days.findIndex(day => day.date === date);
    if (dayIndex === -1) return;

    const updatedPlanner = { ...currentPlanner };
    const day = updatedPlanner.days[dayIndex];
    
    // Find the specific task in timeSlots
    if (day.timeSlots) {
      const taskIndex = day.timeSlots.findIndex(slot => 
        slot.activity === taskActivity && (slot.startTime || slot.time) === taskTime
      );
      
      if (taskIndex !== -1) {
        // Toggle the individual task completion
        day.timeSlots[taskIndex].isCompleted = !day.timeSlots[taskIndex].isCompleted;
        
        try {
          await setDoc(doc(db, 'planners', currentPlanner.id), updatedPlanner);
          setCurrentPlanner(updatedPlanner);
        } catch (error) {
          console.error('Error updating individual task completion:', error);
        }
      }
    }
  };

  // Handler for mobile timeSlot completion - matches the signature expected by MobilePlanner
  const handleTimeSlotToggle = async (task: any, date: string, taskIndex: number) => {
    if (!currentPlanner) return;

    console.log('Toggling mobile timeSlot completion for:', { task, date, taskIndex });

    const updatedDays = currentPlanner.days.map(day => {
      if (day.date === date && day.timeSlots) {
        const updatedTimeSlots = [...day.timeSlots];
        
        // Ensure the taskIndex is valid and within bounds
        if (taskIndex >= 0 && taskIndex < updatedTimeSlots.length) {
          const taskToToggle = updatedTimeSlots[taskIndex];
          
          // Additional validation to ensure we're toggling the right task
          if (taskToToggle.time === task.time && 
              taskToToggle.activity === task.title &&
              taskToToggle.type === task.type) {
            
            updatedTimeSlots[taskIndex] = {
              ...taskToToggle,
              isCompleted: !taskToToggle.isCompleted
            };
            console.log('Mobile task toggled:', updatedTimeSlots[taskIndex]);
          } else {
            console.warn('Mobile task mismatch detected:', { taskToToggle, task });
          }
        } else {
          console.warn('Invalid taskIndex:', taskIndex, 'for slots length:', updatedTimeSlots.length);
        }
        
        return { ...day, timeSlots: updatedTimeSlots };
      }
      return day;
    });

    const updatedPlanner = { ...currentPlanner, days: updatedDays };
    setCurrentPlanner(updatedPlanner);

    try {
      await setDoc(doc(db, 'planners', currentPlanner.id), updatedPlanner);
    } catch (error) {
      console.error('Error toggling mobile task completion:', error);
      // Revert the state if save fails
      setCurrentPlanner(currentPlanner);
    }
  };

    const getAvailableActivities = (taskType: string, assignedMember?: string) => {
    if (!profileData) {
      console.log('DEBUG: No profileData available');
      return [];
    }
    
    switch (taskType) {
      case 'chore':
      case 'task':
        // Both chore and task use the same data source
        let chores: string[] = [];
        
        console.log('DEBUG: Getting chores for member:', assignedMember);
        console.log('DEBUG: Full profileData structure:', profileData);
        console.log('DEBUG: profileData.messTestData:', profileData.messTestData);
        console.log('DEBUG: profileData.messTestData?.choreAssignments:', profileData.messTestData?.choreAssignments);
        
        // Also check other possible locations
        console.log('DEBUG: profileData.choreAssignments:', profileData.choreAssignments);
        console.log('DEBUG: profileData.chore?.assignments:', profileData.chore?.assignments);
        console.log('DEBUG: profileData.chores?.assignments:', profileData.chores?.assignments);
        
        // Try to find chore assignments in different possible locations
        let choreAssignments = null;
        
        if (profileData.messTestData?.choreAssignments && Array.isArray(profileData.messTestData.choreAssignments)) {
          choreAssignments = profileData.messTestData.choreAssignments;
          console.log('DEBUG: Found choreAssignments in messTestData');
        } else if (profileData.choreAssignments && Array.isArray(profileData.choreAssignments)) {
          choreAssignments = profileData.choreAssignments;
          console.log('DEBUG: Found choreAssignments at root level');
        } else if (profileData.chore?.assignments && Array.isArray(profileData.chore.assignments)) {
          choreAssignments = profileData.chore.assignments;
          console.log('DEBUG: Found choreAssignments in chore.assignments');
        } else if (profileData.chores?.assignments && Array.isArray(profileData.chores.assignments)) {
          choreAssignments = profileData.chores.assignments;
          console.log('DEBUG: Found choreAssignments in chores.assignments');
        }
        
        if (choreAssignments) {
          console.log('DEBUG: choreAssignments data:', choreAssignments);
          
          // Filter chores by assigned member
          if (assignedMember) {
            // Get chores assigned to this specific member
            const memberChores: string[] = [];
            choreAssignments.forEach((ca: any) => {
              console.log('DEBUG: Checking assignment:', ca, 'member match:', ca.member === assignedMember);
              if (ca.member === assignedMember && ca.chores && Array.isArray(ca.chores)) {
                // Add all chores from this member's assignment
                memberChores.push(...ca.chores);
              }
            });
            
            // Remove duplicates
            chores = memberChores.filter((chore: string, index: number) => memberChores.indexOf(chore) === index);
            console.log('DEBUG: Chores for', assignedMember, ':', chores);
          } else {
            // No member assigned, show all available chores
            const allChores: string[] = [];
            choreAssignments.forEach((ca: any) => {
              if (ca.chores && Array.isArray(ca.chores)) {
                allChores.push(...ca.chores);
              }
            });
            chores = allChores.filter((chore: string, index: number) => allChores.indexOf(chore) === index);
            console.log('DEBUG: All available chores:', chores);
          }
        } else if (profileData.chores && Array.isArray(profileData.chores)) {
          console.log('DEBUG: Using profileData.chores (no member filtering available)');
          chores = profileData.chores;
        } else {
          console.log('DEBUG: Using default chores (no member filtering available)');
          // Default chores if no data available
          chores = [
            'Make beds',
            'Vacuum living room',
            'Clean bathroom',
            'Wash dishes',
            'Take out trash',
            'Wipe down kitchen counters',
            'Tidy up bedroom',
            'Do laundry'
          ];
        }
        
        console.log('DEBUG: Final chores array:', chores);
        return chores;
         
       case 'lunchbox':
         let lunchboxMeals = [];
         
         if (profileData.food?.lunchboxIdeas && Array.isArray(profileData.food.lunchboxIdeas)) {
           lunchboxMeals = profileData.food.lunchboxIdeas;
         } else if (profileData.lunchboxMeals && Array.isArray(profileData.lunchboxMeals)) {
           lunchboxMeals = profileData.lunchboxMeals;
         } else {
           // Default lunchbox options
           lunchboxMeals = [
             'Sandwich',
             'Salad',
             'Leftovers',
             'Soup',
             'Wrap',
             'Pasta'
           ];
         }
         
         return lunchboxMeals;
         
       case 'dinner':
         let dinnerMeals = [];
         
         if (profileData.food?.favoriteMeals && Array.isArray(profileData.food.favoriteMeals)) {
           dinnerMeals = profileData.food.favoriteMeals;
         } else if (profileData.dinnerMeals && Array.isArray(profileData.dinnerMeals)) {
           dinnerMeals = profileData.dinnerMeals;
         } else {
           // Default dinner options
           dinnerMeals = [
             'Pasta',
             'Rice and vegetables',
             'Chicken dinner',
             'Takeaway',
             'Pizza',
             'Stir fry',
             'Soup and bread'
           ];
         }
         
         return dinnerMeals;
        
      case 'event':
        return []; // Events use custom text input
      default:
        return [];
    }
  };

  const getAssignedMembers = () => {
    if (!profileData) {
      return [];
    }
    
    let members = [];
    
    // Try household.members first (from the test function structure)
    if (profileData.household?.members && Array.isArray(profileData.household.members)) {
      members = profileData.household.members;
    } else if (profileData.householdMembers && Array.isArray(profileData.householdMembers)) {
      members = profileData.householdMembers;
    } else {
      return [];
    }
    
    // Get member colors - debug the structure
    const memberColors = profileData.householdMemberColors || {};
    console.log('DEBUG: memberColors structure:', memberColors);
    console.log('DEBUG: members array:', members);
    
    // Process members to include color information
    const processedMembers = members.map((member: any, index: number) => {
      let memberColor = '#6B7280'; // Default grey
      
      if (typeof member === 'string') {
        // If member is just a string, try different color mapping approaches
        memberColor = memberColors[member] || memberColors[index] || '#6B7280';
        console.log(`DEBUG: Member "${member}" (index ${index}) - color from name: ${memberColors[member]}, color from index: ${memberColors[index]}, final: ${memberColor}`);
        
        return {
          name: member,
          color: memberColor,
          assignChores: true // Assume can be assigned chores
        };
      } else {
        // If member is already an object, try different color mapping approaches
        memberColor = member.color || memberColors[member.name] || memberColors[index] || '#6B7280';
        console.log(`DEBUG: Member object "${member.name}" (index ${index}) - existing color: ${member.color}, color from name: ${memberColors[member.name]}, color from index: ${memberColors[index]}, final: ${memberColor}`);
        
        return {
          ...member,
          color: memberColor,
          assignChores: member.assignChores !== false // Default to true unless explicitly false
        };
      }
    });
    
    console.log('DEBUG: processedMembers:', processedMembers);
    
         // Filter to only members who can be assigned chores
     const filteredMembers = processedMembers.filter((member: any) => {
       return member && member.assignChores;
     });
     
     return filteredMembers;
  };

  const saveTaskEdit = async (updatedTask: TimeSlot) => {
    if (!currentPlanner || !user || !editingTask) return;

    const dayIndex = currentPlanner.days.findIndex(day => day.date === editingTask.date);
    if (dayIndex === -1) return;

    const updatedPlanner = { ...currentPlanner };
    const day = { ...updatedPlanner.days[dayIndex] }; // Create new day object
    updatedPlanner.days[dayIndex] = day;
    
    // Ensure timeSlots array exists
    if (!day.timeSlots) {
      day.timeSlots = [];
    }
    
    if (editingTask.taskIndex === -1) {
      // This is a new task - add it to the timeSlots array
      day.timeSlots = [...day.timeSlots, { ...updatedTask }];
      console.log('DEBUG: Adding new task:', updatedTask);
    } else if (editingTask.taskIndex < day.timeSlots.length) {
      // This is an existing task - update it
      day.timeSlots = [...day.timeSlots];
      day.timeSlots[editingTask.taskIndex] = { ...updatedTask };
      console.log('DEBUG: Updating existing task:', updatedTask);
    }
    
    console.log('DEBUG: Saving task with updated color:', updatedTask.assignedColor, 'for member:', updatedTask.assignedTo);
    
    try {
      await setDoc(doc(db, 'planners', currentPlanner.id), updatedPlanner);
      setCurrentPlanner(updatedPlanner);
      setEditingTask(null); // Close the popup
    } catch (error) {
      console.error('Error updating task:', error);
    }
  };

  const handleTaskSave = async (taskData: any) => {
    if (!currentPlanner || !user) return;
    
    const updatedDays = [...currentPlanner.days];
    const dayIndex = updatedDays.findIndex(day => day.date === taskData.date);
    
    if (dayIndex === -1) return;
    
    // Ensure timeSlots array exists
    if (!updatedDays[dayIndex].timeSlots) {
      updatedDays[dayIndex].timeSlots = [];
    }
    
    const newTask: TimeSlot = {
      time: taskData.startTime,
      startTime: taskData.startTime,
      endTime: taskData.endTime,
      date: taskData.date,
      activity: taskData.title,
      type: taskData.type || 'chore',
      assignedTo: taskData.assignedTo,
      assignedColor: taskData.assignedColor || taskData.color,
      isCompleted: false
    };
    
    // TypeScript safety: ensure timeSlots is defined before pushing
    updatedDays[dayIndex].timeSlots!.push(newTask);
    
    const updatedPlanner = { ...currentPlanner, days: updatedDays };
    
    try {
      await setDoc(doc(db, 'planners', currentPlanner.id), updatedPlanner);
      setCurrentPlanner(updatedPlanner);
    } catch (error) {
      console.error('Error saving task:', error);
    }
  };

  const handleMobileTaskEdit = (task: any) => {
    // Convert mobile task format to desktop editing format
    const today = new Date().toISOString().split('T')[0];
    const dayData = currentPlanner?.days?.find(day => day.date === today);
    
    if (dayData && dayData.timeSlots) {
      const taskIndex = dayData.timeSlots.findIndex(slot => 
        slot.activity === task.title && slot.time === task.time
      );
      
      if (taskIndex !== -1) {
        setEditingTask({
          date: today,
          taskIndex: taskIndex,
          task: dayData.timeSlots[taskIndex]
        });
      }
    }
  };

  const handleAddTask = () => {
    // Create a new task with default values for today
    const newTask: TimeSlot = {
      time: '09:00',
      startTime: '09:00',
      endTime: '',
      activity: '',
      type: 'chore',
      assignedTo: '',
      assignedColor: '#6B7280',
      isCompleted: false
    };
    
    const today = new Date().toISOString().split('T')[0];
    
    console.log('Adding new task:', newTask);
    setEditingTask({
      date: today,
      taskIndex: -1, // -1 indicates this is a new task
      task: newTask
    });
  };

  const navigateDate = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentDate);
    
    if (isMobile) {
      // Navigate by month on mobile
      if (direction === 'next') {
        newDate.setMonth(newDate.getMonth() + 1);
      } else {
        newDate.setMonth(newDate.getMonth() - 1);
      }
    } else {
      // Navigate by week on desktop
      newDate.setDate(newDate.getDate() + (direction === 'next' ? 7 : -7));
    }
    
    setCurrentDate(newDate);
    // Don't reset hasInitiallyScrolled when navigating dates - user chose to navigate
  };

  const goToToday = () => {
    const today = new Date();
    setCurrentDate(today);
    setSelectedDate(today);
    // Reset scroll flag when going to today - user might want to see current time
    setHasInitiallyScrolled(false);
  };

  const getDayTasks = (date: Date) => {
    if (!currentPlanner) return null;
    
    const dateString = date.toISOString().split('T')[0];
    return currentPlanner.days.find(day => day.date === dateString);
  };

  const getDisplayTitle = () => {
    // Always show week view title since we only use dashboard view
    const weekStart = getWeekStart(currentDate);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 6);
    
    if (weekStart.getMonth() === weekEnd.getMonth()) {
      return `${weekStart.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`;
    } else {
      return `${weekStart.toLocaleDateString('en-US', { month: 'short' })} - ${weekEnd.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}`;
    }
  };

  // SVG Icons
  const ChevronLeftIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="15,18 9,12 15,6"></polyline>
    </svg>
  );

  const ChevronRightIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="9,18 15,12 9,6"></polyline>
    </svg>
  );

  const PlusIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="12" y1="5" x2="12" y2="19"></line>
      <line x1="5" y1="12" x2="19" y2="12"></line>
    </svg>
  );

  const CheckIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="20,6 9,17 4,12"></polyline>
    </svg>
  );

  const CleaningIcon = () => (
    <svg viewBox="0 0 24 24" fill="currentColor">
      <path d="M19.36 2.72L20.78 4.14L15.06 9.85C16.13 11.39 16.28 13.24 15.38 14.44L9.06 8.12C10.26 7.22 12.11 7.37 13.65 8.44L19.36 2.72M5.93 17.57C3.92 15.56 2.69 13.16 2.35 10.92L7.23 8.83L14.67 16.27L12.58 21.15C10.34 20.81 7.94 19.58 5.93 17.57Z"/>
    </svg>
  );

  const FoodIcon = () => (
    <svg viewBox="0 0 24 24" fill="currentColor">
      <path d="M8.1 13.34l2.83-2.83L3.91 3.5c-1.56 1.56-1.56 4.09 0 5.66l4.19 4.18zm6.78-1.81c1.53.71 3.68.21 5.27-1.38 1.91-1.91 2.28-4.65.81-6.12-1.46-1.46-4.20-1.10-6.12.81-1.59 1.59-2.09 3.74-1.38 5.27L3.7 19.87l1.41 1.41L12 14.41l6.88 6.88 1.41-1.41L13.41 13l1.47-1.47z"/>
    </svg>
  );

  const DinnerIcon = () => (
    <svg viewBox="0 0 24 24" fill="currentColor">
      <path d="M8.5,8.62V16H7V8.62C6.44,8.15 6.03,7.52 5.8,6.8C5.15,4.65 6.5,2.5 8.5,2.5C10.5,2.5 11.85,4.65 11.2,6.8C10.97,7.52 10.56,8.15 10,8.62V16H8.5M12.5,11H14V16H15.5V11H17L15.75,2.5H13.75L12.5,11Z"/>
    </svg>
  );

  const SearchIcon = ({ className }: { className?: string }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="11" cy="11" r="8"></circle>
      <path d="m21 21-4.35-4.35"></path>
    </svg>
  );

  const PersonIcon = () => (
    <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: '8px', height: '8px', flexShrink: 0 }}>
      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
    </svg>
  );

  const TaskIcon = () => (
    <svg viewBox="0 0 256 202.65" fill="currentColor" style={{ width: '12px', height: '12px', flexShrink: 0 }}>
      <circle cx="21.39" cy="21.39" r="21.39"/>
      <circle cx="21.39" cy="101.33" r="21.39"/>
      <circle cx="21.39" cy="181.26" r="21.39"/>
      <rect x="76.97" y="166.27" width="179.03" height="29.99" rx="14.99" ry="14.99"/>
      <rect x="76.97" y="86.33" width="179.03" height="29.99" rx="14.99" ry="14.99"/>
      <rect x="76.97" y="6.4" width="179.03" height="29.99" rx="14.99" ry="14.99"/>
    </svg>
  );

  const MealIcon = () => (
    <svg viewBox="0 0 256 232.82" fill="currentColor" style={{ width: '12px', height: '12px', flexShrink: 0 }}>
      <path d="M205.72.42c33.18-4.09,53.97,22.23,49.73,54.26-3.53,26.71-30.07,56.62-57.3,60.34-18.63,2.54-31.44-5.2-46.14,12.64l79.23,81.45-21.57,23.71-81.45-81.37-81.45,81.37c-4.76-7-24.42-18.43-20.44-27.06l117.64-117.59C129.68,49.97,168.42,5.02,205.72.42Z"/>
      <path d="M17.67,2.26l86.63,84.98-34.9,36.98C41.38,89.68-34.02,50.28,17.67,2.26Z"/>
    </svg>
  );

  const EventIcon = () => (
    <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: '12px', height: '12px', flexShrink: 0 }}>
      <path d="M19,19H5V8H19M16,1V3H8V1H6V3H5C3.89,3 3,3.89 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5C21,3.89 20.1,5 19,5H18V3M17,12H12V17H17V12Z"/>
    </svg>
  );

  // Helper function to add transparency to colors
  const addTransparency = (color: string, opacity: number = 0.3): string => {
    // If it's already an rgba color, return as is
    if (color.startsWith('rgba')) return color;
    
    // If it's a hex color, convert to rgba
    if (color.startsWith('#')) {
      const hex = color.replace('#', '');
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);
      return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    }
    
    // If it's an rgb color, convert to rgba
    if (color.startsWith('rgb(')) {
      const values = color.match(/\d+/g);
      if (values && values.length >= 3) {
        return `rgba(${values[0]}, ${values[1]}, ${values[2]}, ${opacity})`;
      }
    }
    
    // Fallback for other color formats
    return color;
  };

  // Helper function to check if a date is in the currently viewed week
  function isViewingWeek(date: Date): boolean {
    const currentWeekStart = getWeekStart(currentDate);
    const dateWeekStart = getWeekStart(date);
    return currentWeekStart.toDateString() === dateWeekStart.toDateString();
  }

  // Removed renderMonthView - only using dashboard view now

  // Removed renderWeekView - only using dashboard view now

  const renderMiniCalendar = () => {
    const monthDays = getMonthDays(currentDate);
    const dayNames = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
    
    return (
      <MiniCalendar>
        <div className="calendar-grid">
          {dayNames.map((day, index) => (
            <div key={`day-${index}`} className="day-header">{day}</div>
          ))}
          {monthDays.map((date, index) => {
            const isCurrentMonth = date.getMonth() === currentDate.getMonth();
            const todayCheck = isToday(date);
            const isSelected = date.toDateString() === selectedDate.toDateString();
            const isInViewingWeek = isViewingWeek(date);
            
            return (
              <div
                key={index}
                className={`day-cell ${!isCurrentMonth ? 'other-month' : ''} ${todayCheck ? 'today' : ''} ${isSelected ? 'selected' : ''} ${isInViewingWeek ? 'viewing-week' : ''}`}
                onClick={() => setSelectedDate(date)}
              >
                {date.getDate()}
              </div>
            );
          })}
        </div>
      </MiniCalendar>
    );
  };

  const toggleMemberFilter = (memberName: string) => {
    setSelectedMembers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(memberName)) {
        newSet.delete(memberName);
      } else {
        newSet.add(memberName);
      }
      return newSet;
    });
  };

  const toggleAllMembers = () => {
    const assignedMembers = getAssignedMembers();
    const allMembers = [...assignedMembers.map((member: any) => member.name), 'Unassigned'];
    const allSelected = allMembers.every((member: string) => selectedMembers.has(member));
    
    if (allSelected) {
      setSelectedMembers(new Set());
    } else {
      setSelectedMembers(new Set(allMembers));
    }
  };

  const getMemberCount = (memberName: string): string => {
    if (!currentPlanner) return '0';
    
    const weekDays = getWeekDays(currentDate);
    let count = 0;
    
    weekDays.forEach(day => {
      const dayTasks = getDayTasks(day);
      if (dayTasks && dayTasks.timeSlots) {
        if (memberName === '' || memberName === 'Unassigned') {
          // Count unassigned tasks (empty or undefined assignedTo)
          count += dayTasks.timeSlots.filter(slot => !slot.assignedTo || slot.assignedTo === '').length;
        } else {
          // Count tasks assigned to this specific member
          count += dayTasks.timeSlots.filter(slot => slot.assignedTo === memberName).length;
        }
      }
    });
    
    return count.toString();
  };

  const renderFilters = () => {
    const assignedMembers = getAssignedMembers();
    const members = [
      ...assignedMembers.map((member: any) => ({
        name: member.name,
        color: member.color,
        count: getMemberCount(member.name)
      })),
      // Add "Unassigned" option
      {
        name: 'Unassigned',
        color: '#9CA3AF', // Gray color for unassigned tasks
        count: getMemberCount('')
      }
    ];

    const allMembers = members.map((member: any) => member.name);
    const allSelected = allMembers.every((member: string) => selectedMembers.has(member));

    return (
      <FilterSection>
        <div className="filter-group">
          <div className="filter-header">
            <span className="toggle-all-btn" onClick={toggleAllMembers}>
              {allSelected ? 'Deselect All' : 'Select All'}
            </span>
          </div>
          {members.map((member: any) => {
            const isSelected = selectedMembers.has(member.name);
            return (
              <div 
                key={member.name} 
                className={`filter-item ${isSelected ? 'selected' : 'unselected'}`}
                onClick={() => toggleMemberFilter(member.name)}
              >
                <div className={`filter-checkbox ${isSelected ? 'checked' : ''}`}></div>
                <div className="filter-color" style={{ background: member.color }}></div>
                <span className="filter-text">{member.name}</span>
                <span className="filter-count">{member.count}</span>
              </div>
            );
          })}
        </div>
      </FilterSection>
    );
  };

  const renderUpcomingEvents = () => {
    if (!currentPlanner) return null;
    
    // Get current date and 2 weeks from now
    const today = new Date();
    const twoWeeksFromNow = new Date();
    twoWeeksFromNow.setDate(today.getDate() + 14);
    
    // Find all events in the next 2 weeks
    const upcomingEvents: Array<{ 
      event: TimeSlot; 
      date: string; 
      dateObj: Date;
      dayName: string;
    }> = [];
    
    // Check all days in current planner and potentially next week's planner
    currentPlanner.days.forEach(day => {
      const dayDate = new Date(day.date);
      
      // Only include dates from today onwards and within 2 weeks
      if (dayDate >= today && dayDate <= twoWeeksFromNow && day.timeSlots) {
        day.timeSlots
          .filter(slot => slot.type === 'event') // Only show events
          .forEach(slot => {
            upcomingEvents.push({
              event: slot,
              date: day.date,
              dateObj: dayDate,
              dayName: dayDate.toLocaleDateString('en-US', { weekday: 'short' })
            });
          });
      }
    });
    
    // Sort events by date and time
    upcomingEvents.sort((a, b) => {
      const dateCompare = a.dateObj.getTime() - b.dateObj.getTime();
      if (dateCompare !== 0) return dateCompare;
      
      // If same date, sort by time
      const timeA = a.event.startTime || a.event.time;
      const timeB = b.event.startTime || b.event.time;
      return timeA.localeCompare(timeB);
    });
    
    // Take only the next 5 events to avoid overcrowding
    const limitedEvents = upcomingEvents.slice(0, 5);
    
    if (limitedEvents.length === 0) {
      return (
        <PrioritiesSection>
          <div style={{ 
            padding: '16px',
            textAlign: 'center',
            color: theme.colors.lightText,
            fontSize: '14px'
          }}>
            No upcoming events in the next 2 weeks
          </div>
        </PrioritiesSection>
      );
    }

    return (
      <PrioritiesSection>
        {limitedEvents.map((item, index) => {
          const isToday = item.dateObj.toDateString() === today.toDateString();
          const isTomorrow = item.dateObj.toDateString() === new Date(today.getTime() + 24 * 60 * 60 * 1000).toDateString();
          
          let dateDisplay = '';
          if (isToday) {
            dateDisplay = 'Today';
          } else if (isTomorrow) {
            dateDisplay = 'Tomorrow';
          } else {
            dateDisplay = `${item.dayName} ${item.dateObj.getDate()}`;
          }
          
          return (
            <div key={index} className="priority-item">
              <div className="priority-icon" style={{ 
                background: item.event.assignedColor || theme.colors.primary,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '12px'
              }}>
                📅
              </div>
              <div className="priority-content">
                <div className="priority-title">{item.event.activity}</div>
                <div className="priority-method">
                  {dateDisplay} • {item.event.startTime || item.event.time}
                  {item.event.endTime && ` - ${item.event.endTime}`}
                </div>
              </div>
            </div>
          );
        })}
      </PrioritiesSection>
    );
  };

  const renderDashboardWeekView = () => {
    const weekDays = getWeekDays(currentDate);
    const dayNames = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
    const timeSlots = Array.from({ length: 18 }, (_, i) => {
      const hour = i + 5; // Start from 5 AM to 10 PM (18 hours)
      const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
      const ampm = hour >= 12 ? 'PM' : 'AM';
      return { hour, display: `${displayHour.toString().padStart(2, '0')}:00` };
    });

    // Check if no members are selected
    const noMembersSelected = selectedMembers.size === 0;

    if (noMembersSelected) {
      return (
        <WeekViewContainer>
          <EmptyFilterState>
            <SearchIcon className="empty-icon" />
            <div className="empty-title">No members selected</div>
            <div className="empty-subtitle">Select household members from the sidebar to view their tasks</div>
          </EmptyFilterState>
        </WeekViewContainer>
      );
    }

    return (
      <WeekViewContainer>
        <WeekHeader>
          <TimeColumnHeader></TimeColumnHeader>
          {weekDays.map((date, index) => {
            const todayCheck = isToday(date);
            return (
              <DayHeader key={index} isToday={todayCheck}>
                <div className="day-name">{dayNames[index]}</div>
                <div className="day-number">{date.getDate()}</div>
              </DayHeader>
            );
          })}
        </WeekHeader>
                <WeekGrid className="week-grid-container" style={{ position: 'relative' }}>
          {isCurrentTimeVisible() && currentTime.getHours() >= 5 && currentTime.getHours() < 23 && (() => {
            // Find today's day index in the week
            const todayIndex = weekDays.findIndex(day => isToday(day));
            return todayIndex !== -1 ? (
              <CurrentTimeLine top={getCurrentTimeLineTop()} dayIndex={todayIndex} />
            ) : null;
          })()}
          {timeSlots.map((timeSlot, timeIndex) => (
            <React.Fragment key={timeIndex}>
              <TimeSlot>{timeSlot.display}</TimeSlot>
              {weekDays.map((day, dayIndex) => {
                const dayTasks = getDayTasks(day);
                const todayCheck = isToday(day);
                return (
                  <DayColumn
                    key={`${timeIndex}-${dayIndex}`}
                    isToday={todayCheck}
                    isPast={isPast(day)}
                    onClick={() => {
                      setSelectedDate(day);
                    }}
                    style={{
                      position: 'relative', // Required for absolute positioning of tasks
                      overflow: 'visible' // Allow tasks to extend beyond their starting slot
                    }}
                  >
                    {(() => {
                      if (!dayTasks) return null;
                      
                      // Helper function to get icon for task type
                      const getTaskIcon = (type: string) => {
                        switch (type) {
                          case 'chore':
                          case 'task':
                            return <TaskIcon />;
                          case 'lunchbox':
                          case 'dinner':
                            return <MealIcon />;
                          case 'event':
                            return <EventIcon />;
                          default:
                            return <TaskIcon />;
                        }
                      };
                      
                      // Helper function to check if task should be shown based on member filter
                      const shouldShowTask = (assignedTo: string | undefined) => {
                        // If no members are selected, show all tasks
                        if (selectedMembers.size === 0) return true;
                        
                        // If task is not assigned to anyone, show it only if "Unassigned" is selected
                        if (!assignedTo) {
                          return selectedMembers.has('Unassigned');
                        }
                        
                        // Show task if the assigned member is selected
                        return selectedMembers.has(assignedTo);
                      };
                      
                      // Find tasks that START in this time slot (for single continuous display)
                      const tasksForThisTime = dayTasks.timeSlots?.filter(slot => {
                        if (!shouldShowTask(slot.assignedTo)) return false;
                        
                        const startTime = slot.startTime || slot.time;
                        const eventHour = parseInt(startTime.split(':')[0]);
                        
                        // Only show tasks that actually start in this time slot
                        return eventHour === timeSlot.hour;
                      }) || [];
                      
                      // Debug: Log what tasks are found for this time slot
                      if (tasksForThisTime.length > 0) {
                        console.log(`Time slot ${timeSlot.hour}:00 has ${tasksForThisTime.length} tasks:`, 
                          tasksForThisTime.map((t: any) => ({ activity: t.activity, type: t.type, time: t.time }))
                        );
                      }
                      
                      return tasksForThisTime.map((task: any, taskIndex: number) => {
                        // Group tasks by exact start time for width calculation
                        const currentTaskTime = task.startTime || task.time;
                        const tasksAtSameTime = tasksForThisTime.filter((t: any) => 
                          (t.startTime || t.time) === currentTaskTime
                        );
                        
                        // Calculate the index within the same-time group for proper side-by-side positioning
                        // Use the taskIndex within the filtered tasksForThisTime array to determine relative position
                        const sameTimeIndex = tasksAtSameTime.findIndex(t => 
                          t === task // Use object reference for exact match
                        );
                        
                        // Debug: Log positioning data
                        console.log(`Task positioning - ${task.activity}:`, {
                          taskIndex,
                          sameTimeIndex,
                          tasksAtSameTimeLength: tasksAtSameTime.length,
                          currentTaskTime,
                          leftPosition: tasksAtSameTime.length > 1 ? `${(100 / tasksAtSameTime.length) * sameTimeIndex + 1}%` : '1px',
                          width: tasksAtSameTime.length > 1 ? `${(100 / tasksAtSameTime.length) - 2}%` : 'calc(100% - 2px)'
                        });
                        
                        // Debug: Log task data to see what's being rendered
                        console.log(`Rendering task ${taskIndex}:`, {
                          activity: task.activity,
                          type: task.type,
                          assignedTo: task.assignedTo,
                          time: task.time,
                          startTime: task.startTime
                        });
                        
                        // Use individual task completion status
                        const isTaskCompleted = task.isCompleted || false;
                        
                        // Calculate task duration for sizing
                        const calculateDuration = (startTime: string, endTime: string) => {
                          const [startHour, startMinute] = startTime.split(':').map(Number);
                          const [endHour, endMinute] = endTime.split(':').map(Number);
                          const startMinutes = startHour * 60 + startMinute;
                          const endMinutes = endHour * 60 + endMinute;
                          return Math.max(15, endMinutes - startMinutes); // Minimum 15 minutes
                        };
                        
                        // Calculate position and size for the full task (spanning multiple slots if needed)
                        const calculatePositionAndSize = (startTime: string, currentSlotHour: number, endTime?: string) => {
                          const [startHour, startMinute] = startTime.split(':').map(Number);
                          const taskStartMinutes = startHour * 60 + startMinute;
                          
                          const slotStartMinutes = currentSlotHour * 60;
                          
                          if (!endTime) {
                            // No end time - show from start position to end of current slot
                            const minuteOffset = Math.max(0, taskStartMinutes - slotStartMinutes);
                            const topOffsetPercent = (minuteOffset / 60) * 100;
                            const remainingMinutes = 60 - minuteOffset;
                            const heightPercent = (remainingMinutes / 60) * 100;
                            
                            return {
                              topOffsetPercent,
                              heightPercent,
                              duration: remainingMinutes
                            };
                          }
                          
                          const [endHour, endMinute] = endTime.split(':').map(Number);
                          const taskEndMinutes = endHour * 60 + endMinute;
                          
                          // Calculate the full task duration and height
                          const fullDuration = taskEndMinutes - taskStartMinutes;
                          
                          // Position within the starting slot (0-100%)
                          const topOffsetPercent = ((taskStartMinutes - slotStartMinutes) / 60) * 100;
                          
                          // Height as a multiple of time slots (can exceed 100% to span multiple slots)
                          const heightPercent = (fullDuration / 60) * 100;
                          
                          return {
                            topOffsetPercent,
                            heightPercent,
                            duration: fullDuration
                          };
                        };
                        
                        const duration = task.startTime && task.endTime 
                          ? calculateDuration(task.startTime, task.endTime)
                          : 60; // Default 60 minutes
                        
                        // Get positioning and sizing for this task
                        const { topOffsetPercent, heightPercent } = calculatePositionAndSize(
                          task.startTime || task.time, 
                          timeSlot.hour,
                          task.endTime
                        );
                        
                        // Calculate actual pixel values
                        const timeSlotHeight = 80; // DayColumn height
                        const taskHeight = Math.max(20, (heightPercent / 100) * timeSlotHeight); // Minimum 20px height to maintain proportional sizing
                        const topOffset = (topOffsetPercent / 100) * timeSlotHeight;
                        
                        return (
                          <EventItem
                            key={`${task.activity}-${timeSlot.hour}-${taskIndex}`}
                            type={task.type === 'task' || task.type === 'event' ? 'chore' : task.type as 'chore' | 'lunchbox' | 'dinner'}
                            completed={isTaskCompleted}
                            isPast={isPast(day)}
                            style={{ 
                              borderLeft: `4px solid ${task.assignedColor || '#6B7280'}`,
                              backgroundColor: addTransparency(task.assignedColor || '#6B7280', 0.15), // Light member color background - opaque enough to block grey lines
                              color: task.assignedColor || '#6B7280',
                              height: `${taskHeight}px`,
                              minHeight: `${Math.max(20, taskHeight)}px`,
                              position: 'absolute',
                              top: `${topOffset}px`,
                              left: tasksAtSameTime.length > 1 ? 
                                `${(100 / tasksAtSameTime.length) * sameTimeIndex + 1}%` : // Added 1% margin for side-by-side
                                '1px', // Small left margin
                              width: tasksAtSameTime.length > 1 ? 
                                `${(100 / tasksAtSameTime.length) - 2}%` : // Reduced by 2% for margins between tiles
                                'calc(100% - 2px)', // Full width minus margins for single tasks
                              right: 'auto', // Let width handle the sizing
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'flex-start',
                              padding: tasksAtSameTime.length > 1 ? '2px 4px' : (todayCheck ? '4px 8px 4px 4px' : '4px'), // Tighter padding for side-by-side
                              fontSize: tasksAtSameTime.length > 1 ? '9px' : '10px', // Smaller font for side-by-side
                              opacity: isTaskCompleted ? 0.7 : 1,
                              boxSizing: 'border-box',
                              minWidth: tasksAtSameTime.length > 1 ? '40px' : '0', // Minimum width for side-by-side readability
                              maxWidth: '100%', // Prevent container overflow
                              zIndex: 10, // High z-index to appear above adjacent time slots
                              borderRadius: '4px', // Smaller radius for compact view
                              boxShadow: '0 1px 3px rgba(0,0,0,0.1)', // Subtle shadow for depth
                              overflow: 'visible', // Allow text to be visible for side-by-side
                              wordBreak: tasksAtSameTime.length > 1 ? 'break-word' : 'normal', // Break words for narrow side-by-side tiles
                              hyphens: tasksAtSameTime.length > 1 ? 'auto' : 'none', // Auto hyphenation for side-by-side
                              textAlign: tasksAtSameTime.length > 1 ? 'center' : 'left' // Center text for narrow tiles
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              // Open task editing popup
                              const taskIndex = dayTasks.timeSlots?.findIndex(slot => 
                                slot.activity === task.activity && (slot.startTime || slot.time) === (task.startTime || task.time)
                              ) ?? -1;
                              
                              if (taskIndex !== -1) {
                                setEditingTask({
                                  date: dayTasks.date,
                                  taskIndex,
                                  task: { ...task }
                                });
                              }
                            }}
                          >
                            {/* Checkbox for today's tasks only */}
                            {todayCheck && (
                              <div
                                style={{
                                  position: 'absolute',
                                  top: '2px',
                                  right: '2px',
                                  width: '18px',
                                  height: '18px',
                                  borderRadius: '4px',
                                  border: '2px solid rgba(255, 255, 255, 0.9)',
                                  backgroundColor: isTaskCompleted ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 255, 255, 0.1)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  cursor: 'pointer',
                                  zIndex: 15,
                                  transition: 'all 0.2s ease',
                                  fontSize: '10px',
                                  flexShrink: 0,
                                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)'
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  // Toggle individual task completion
                                  toggleIndividualTaskCompletion(
                                    dayTasks.date, 
                                    task.activity, 
                                    task.startTime || task.time
                                  );
                                }}
                                onMouseOver={(e) => {
                                  const target = e.target as HTMLElement;
                                  target.style.backgroundColor = isTaskCompleted 
                                    ? 'rgba(255, 255, 255, 0.8)' 
                                    : 'rgba(255, 255, 255, 0.3)';
                                  target.style.transform = 'scale(1.1)';
                                  target.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.3)';
                                }}
                                onMouseOut={(e) => {
                                  const target = e.target as HTMLElement;
                                  target.style.backgroundColor = isTaskCompleted 
                                    ? 'rgba(255, 255, 255, 0.95)' 
                                    : 'rgba(255, 255, 255, 0.1)';
                                  target.style.transform = 'scale(1)';
                                  target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.2)';
                                }}
                              >
                                {isTaskCompleted && (
                                  <span style={{ color: task.assignedColor || '#6B7280', fontWeight: 'bold' }}>✓</span>
                                )}
                              </div>
                            )}
                            {/* Task content container - handles both single and side-by-side tiles */}
                            <div style={{ 
                              position: 'static',
                              color: task.assignedColor || '#6B7280',
                              zIndex: 5,
                              display: 'flex',
                              flexDirection: 'row', // Always use row layout - icon and text side by side
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: tasksAtSameTime.length > 1 ? '2px' : '4px', // Smaller gap for side-by-side tiles
                              height: '100%',
                              width: '100%',
                              padding: tasksAtSameTime.length > 1 ? '3px 2px' : '4px',
                              textAlign: 'center',
                              boxSizing: 'border-box',
                              overflow: 'hidden' // Ensure content stays within tile bounds
                            }}>
                              {getTaskIcon(task.type)}
                              {/* Description for both single and side-by-side tasks */}
                              <span style={{
                                fontSize: tasksAtSameTime.length > 1 ? 
                                  (taskHeight > 50 ? '10px' : taskHeight > 35 ? '9px' : '8px') : 
                                  (taskHeight > 30 ? '11px' : '9px'),
                                fontWeight: '600',
                                lineHeight: tasksAtSameTime.length > 1 ? '1.2' : '1.2',
                                color: task.assignedColor || '#6B7280',
                                textAlign: 'center',
                                overflow: 'hidden',
                                display: '-webkit-box',
                                WebkitLineClamp: tasksAtSameTime.length > 1 ? 
                                  (taskHeight > 50 ? 3 : taskHeight > 35 ? 2 : 1) : 
                                  (taskHeight > 40 ? 3 : taskHeight > 25 ? 2 : 1),
                                WebkitBoxOrient: 'vertical',
                                wordBreak: 'break-word',
                                hyphens: 'auto',
                                maxWidth: tasksAtSameTime.length > 1 ? 'calc(100% - 20px)' : '100%', // Account for icon width in side-by-side
                                minHeight: 'auto',
                                flex: tasksAtSameTime.length > 1 ? '1 1 0' : 'none', // Allow text to take remaining space for side-by-side
                                minWidth: '0' // Allow text to shrink if needed
                              }}>
                                {task.activity || 'No activity set'}
                              </span>
                            </div>

                            {/* Duration badge in bottom left corner - avoid checkbox conflicts */}
                            {task.endTime && tasksAtSameTime.length === 1 && taskHeight > 25 && (
                              <div style={{ 
                                position: 'absolute',
                                bottom: '3px',
                                left: '3px', // Moved to left side to avoid checkbox
                                fontSize: '8px', 
                                color: task.assignedColor || '#6B7280',
                                fontWeight: '600',
                                backgroundColor: addTransparency(task.assignedColor || '#6B7280', 0.15),
                                padding: '2px 4px',
                                borderRadius: '4px',
                                zIndex: 5,
                                maxWidth: '40px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                textAlign: 'center'
                              }}>
                                {(() => {
                                  const start = task.startTime || task.time;
                                  const end = task.endTime;
                                  if (start && end) {
                                    const [startH, startM] = start.split(':').map(Number);
                                    const [endH, endM] = end.split(':').map(Number);
                                    const duration = (endH * 60 + endM) - (startH * 60 + startM);
                                    return `${duration}min`;
                                  }
                                  return '';
                                })()}
                              </div>
                            )}

                            {/* Time badge in top right (for larger tiles) */}
                            {tasksAtSameTime.length === 1 && taskHeight > 45 && (
                              <div style={{ 
                                position: 'absolute',
                                top: '3px',
                                right: todayCheck ? '26px' : '3px', // Account for larger checkbox
                                fontSize: '9px', 
                                fontWeight: '600',
                                color: task.assignedColor || '#6B7280',
                                backgroundColor: addTransparency(task.assignedColor || '#6B7280', 0.15),
                                padding: '2px 5px',
                                borderRadius: '6px',
                                minWidth: '32px',
                                textAlign: 'center',
                                zIndex: 5,
                                maxWidth: '50px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}>
                                {task.startTime}
                              </div>
                            )}



                            {/* Compact footer - hide for all tasks since info is now inline */}
                            <div style={{ 
                              display: 'none' // Hidden since we moved content inline
                            }}>
                              {task.assignedTo && (
                                <div style={{ 
                                  fontSize: tasksAtSameTime.length > 1 ? '9px' : '10px', 
                                  color: task.assignedColor || '#6B7280',
                                  fontWeight: '500',
                                  backgroundColor: addTransparency(task.assignedColor || '#6B7280', 0.1),
                                  padding: '1px 2px',
                                  borderRadius: '2px',
                                  maxWidth: '100%',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  gap: '1px'
                                }}>
                                  <PersonIcon /> 
                                  <span style={{ fontSize: tasksAtSameTime.length > 1 ? '9px' : '10px' }}>
                                    {task.assignedTo}
                                  </span>
                                </div>
                              )}
                              {tasksAtSameTime.length > 1 && (
                                <div style={{ 
                                  fontSize: '8px', 
                                  color: task.assignedColor || '#6B7280',
                                  fontWeight: '500',
                                  textAlign: 'center'
                                }}>
                                  {task.startTime}
                                </div>
                              )}

                            </div>
                          </EventItem>
                        );
                      });
                    })()}
                  </DayColumn>
                );
              })}
            </React.Fragment>
          ))}
        </WeekGrid>
      </WeekViewContainer>
    );
  };

  const renderTaskEditPopup = () => {
    if (!editingTask) return null;

    const TaskEditPopup = () => {
      const [editedTask, setEditedTask] = useState<TimeSlot>(editingTask.task);
      
      console.log('DEBUG: TaskEditPopup - Initial editedTask:', editedTask);
      console.log('DEBUG: TaskEditPopup - editedTask.type:', editedTask.type);
      console.log('DEBUG: TaskEditPopup - editedTask.assignedTo:', editedTask.assignedTo);
      console.log('DEBUG: TaskEditPopup - editedTask.assignedColor:', editedTask.assignedColor);
      
      // Recalculate available activities when task type or assigned member changes
      const availableActivities = getAvailableActivities(editedTask.type, editedTask.assignedTo);
      console.log('DEBUG: TaskEditPopup - availableActivities:', availableActivities);
      
      const assignedMembers = getAssignedMembers();
      console.log('DEBUG: TaskEditPopup - assignedMembers:', assignedMembers);

      return (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => setEditingTask(null)}
        >
          <div
            style={{
              backgroundColor: 'white',
              padding: '24px',
              borderRadius: '12px',
              width: '400px',
              maxWidth: '90vw',
              maxHeight: '80vh',
              overflow: 'auto',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '600' }}>
              {editingTask.taskIndex === -1 ? 'New Task' : 'Edit Task'}
            </h3>

            {/* Date */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                Date
              </label>
              <input
                type="date"
                value={editedTask.date || ''}
                onChange={(e) => setEditedTask({ ...editedTask, date: e.target.value })}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>

            {/* Time Fields */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                Time
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#6b7280' }}>
                    Start Time
                  </label>
                  <input
                    type="time"
                    value={editedTask.startTime || editedTask.time}
                    onChange={(e) => setEditedTask({ 
                      ...editedTask, 
                      startTime: e.target.value,
                      time: e.target.value
                    })}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#6b7280' }}>
                    End Time
                  </label>
                  <input
                    type="time"
                    value={editedTask.endTime || ''}
                    onChange={(e) => setEditedTask({ ...editedTask, endTime: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Assigned To */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                Assigned To
              </label>
                             <select
                 value={editedTask.assignedTo || ''}
                 onChange={(e) => {
                   const selectedMember = assignedMembers.find((member: any) => member.name === e.target.value);
                   console.log('DEBUG: Selected member:', e.target.value);
                   console.log('DEBUG: Found member object:', selectedMember);
                   console.log('DEBUG: Member color:', selectedMember?.color);
                   console.log('DEBUG: All assigned members:', assignedMembers);
                   
                   const newColor = selectedMember?.color || '#6B7280';
                   console.log('DEBUG: Setting assignedColor to:', newColor);
                   
                   setEditedTask({
                     ...editedTask,
                     assignedTo: e.target.value,
                     assignedColor: newColor,
                     activity: '' // Reset activity when member changes since available chores will be different
                   });
                 }}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
                             >
                 <option value="">Unassigned</option>
                 {Array.isArray(assignedMembers) && assignedMembers.map((member: any) => (
                   <option key={member.name} value={member.name}>
                     {member.name}
                   </option>
                 ))}
               </select>
            </div>

            {/* Task Type */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                Task Type
              </label>
                             <select
                 value={editedTask.type}
                 onChange={(e) => setEditedTask({
                   ...editedTask,
                   type: e.target.value as TimeSlot['type'],
                   activity: '' // Reset activity when type changes
                 })}
                 style={{
                   width: '100%',
                   padding: '8px 12px',
                   border: '1px solid #d1d5db',
                   borderRadius: '6px',
                   fontSize: '14px'
                 }}
               >
                 <option value="chore">Task/Chore</option>
                 <option value="lunchbox">Lunchbox</option>
                 <option value="dinner">Dinner</option>
                 <option value="event">Event</option>
               </select>
            </div>

            {/* Activity */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                Activity
              </label>
              {editedTask.type === 'event' ? (
                <input
                  type="text"
                  value={editedTask.activity}
                  onChange={(e) => setEditedTask({ ...editedTask, activity: e.target.value })}
                  placeholder="Enter event description"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              ) : (
                <select
                  value={editedTask.activity}
                  onChange={(e) => setEditedTask({ ...editedTask, activity: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                                 >
                   <option value="">Select activity</option>
                   {Array.isArray(availableActivities) && availableActivities.map((activity: string) => (
                     <option key={activity} value={activity}>
                       {activity}
                     </option>
                   ))}
                 </select>
              )}
            </div>

            {/* Buttons */}
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setEditingTask(null)}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  backgroundColor: 'white',
                  color: '#374151',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  console.log('DEBUG: About to save task:', editedTask);
                  console.log('DEBUG: Task assignedTo:', editedTask.assignedTo);
                  console.log('DEBUG: Task assignedColor:', editedTask.assignedColor);
                  saveTaskEdit(editedTask);
                }}
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  borderRadius: '6px',
                  backgroundColor: theme.colors.primary,
                  color: 'white',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      );
    };

    return <TaskEditPopup />;
  };

  const weekStart = getWeekStart(currentDate);
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekEnd.getDate() + 6);

  return (
    <ResponsivePlanner
      plannerData={currentPlanner}
      onTaskToggle={toggleTaskCompletion}
      onTimeSlotToggle={handleTimeSlotToggle}
      onTaskEdit={handleMobileTaskEdit}
      onTaskSave={handleTaskSave}
      onDateNavigate={navigateDate}
      currentDate={currentDate}
      selectedMembers={selectedMembers}
      householdMembers={getAssignedMembers()}
      profileData={profileData}
    >
      <DashboardContainer>
        <DashboardHeader>
          <HeaderTitle>
            <WeekNavigation>
              <NavButton onClick={() => navigateDate('prev')}>
                <ChevronLeftIcon />
              </NavButton>
              <WeekTitle>
                {weekStart.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })} - {weekEnd.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </WeekTitle>
              <NavButton onClick={() => navigateDate('next')}>
                <ChevronRightIcon />
              </NavButton>
            </WeekNavigation>
          </HeaderTitle>
          <div style={{ display: 'flex', gap: '12px' }}>
            {!isCurrentWeek(currentDate) && (
              <TodayButton onClick={goToToday}>
                Today
              </TodayButton>
            )}

            <CreateButton onClick={handleAddTask} title="Add new task">
              +
            </CreateButton>
          </div>
        </DashboardHeader>

        <DashboardContent>
          <Sidebar>
            <SidebarCard>
              <SidebarTitle>{currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</SidebarTitle>
              {renderMiniCalendar()}
            </SidebarCard>

            <SidebarCard>
              {renderFilters()}
            </SidebarCard>

            <SidebarCard>
              <SidebarTitle>Upcoming Events</SidebarTitle>
              {renderUpcomingEvents()}
            </SidebarCard>
          </Sidebar>

          <MainContent>
            {renderDashboardWeekView()}
          </MainContent>
        </DashboardContent>
        
        {/* Task Edit Popup */}
        {renderTaskEditPopup()}
      </DashboardContainer>
    </ResponsivePlanner>
  );
};

export default Planner;